import { socialPayload } from '../utils/clientSocial';
import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import { usePermissionsStore } from './permissionsStore';
import { applyLeadVisibilityToQuery, isLeadVisible, LEAD_NOT_VISIBLE_MESSAGE } from '../utils/leadVisibility';
import { useClientStateChangesStore } from './clientStateChangesStore';
import { useClientTagStore } from './clientTagStore';
import { useCustomStatesStore } from './customStatesStore';
import { eventBus } from '../events/eventBus';
import { EXCLUDE_REFERENTI } from '../utils/contactPollution';
import { buildPhoneDigitPatterns } from '../utils/phoneSearch';
import { sanitizeFilterValue, allWordsClause } from '../utils/nameSearch';
import { fetchReferentiAsContacts, mergeContactsByEmail } from '../utils/fetchReferentiContacts';
import type { Client, ClientFormData, LeadsPaginationState, StagePaginationState, LeadsFilters } from '../types';
import { getCachedClientStates, getCachedLeadSources, getCurrentUserOrganizationId } from './referenceDataCache';
import { newsletterListSyncService } from '../services/newsletterListSyncService';
import { geocodeAddress } from '../services/geocodingService';
import { dispatchTaskCompleted } from '../onboarding/dispatchTaskCompleted';
import { isoFromCountryName } from '../utils/countries';
import { resolveNewLeadPipeline } from '../leads/newLeadPipelineDefaults';

/**
 * Pipeline e fase in cui mettere un lead nuovo quando chi lo crea non ha scelto.
 * Restituisce null per le organizzazioni che le pipeline non le usano: li' i lead
 * vivono negli stati e non va toccato niente.
 */
async function resolveDefaultPipelineForOrg(organizationId: string) {
  const [{ data: pipelines }, { data: stages }] = await Promise.all([
    supabase
      .from('lead_pipelines')
      .select('id, is_active, is_default, is_locked, sort_order')
      .eq('organization_id', organizationId),
    supabase
      .from('lead_pipeline_stages')
      .select('id, pipeline_id, is_active, sort_order')
      .eq('organization_id', organizationId),
  ]);
  return resolveNewLeadPipeline(pipelines || [], stages || []);
}

/**
 * Filtro `or` della ricerca cliente/lead.
 *
 * Oltre alle colonne di sempre (ragione sociale, insegna, partita IVA, email, telefono)
 * aggiunge, quando il testo cercato ha più parole, una clausola che le richiede TUTTE
 * dentro `business_name` in qualsiasi ordine. Serve alle persone fisiche: senza, cercare
 * "Mario Rossi" non trova il paziente salvato come "Rossi Mario" (caso Èlan, 3 set 2026).
 *
 * `safeTerm` è già ripulito dai caratteri di sintassi PostgREST; il termine grezzo serve
 * solo al riconoscimento del telefono, che lavora sulle cifre e ha la sua normalizzazione.
 */
function buildClientSearchOr(safeTerm: string, rawTerm: string): string {
  const parts = [
    `business_name.ilike.%${safeTerm}%`,
    `trade_name.ilike.%${safeTerm}%`,
    `vat_number.ilike.%${safeTerm}%`,
    `email.ilike.%${safeTerm}%`,
    ...buildPhoneDigitPatterns(rawTerm).map((p) => `phone_digits.ilike.%${p}%`),
  ];
  const perParole = allWordsClause('business_name', safeTerm);
  if (perParole) parts.push(perParole);
  return parts.join(',');
}

// Helper: determina lo stage effettivo di un lead nella kanban corrente.
// Per lead extra (da junction table), usa _membershipStageId.
// Per lead primari, usa pipeline_stage_id o lead_stage (legacy).
function getEffectiveStageId(lead: Client): string | undefined | null {
  if (lead._isExtraPipeline && lead._membershipStageId) {
    return lead._membershipStageId;
  }
  return lead.pipeline_id ? lead.pipeline_stage_id : lead.lead_stage;
}

interface ClientsPaginationState {
  offset: number;
  hasMore: boolean;
  totalCount: number | null;
  loading: boolean;
}

interface SelectableClient {
  id: string;
  business_name: string;
  entity_type: string;
  vat_number: string | null;
  tax_code: string | null;
}

interface ClientStore {
  clients: Client[];
  leads: Client[];
  selectedClient: Client | null;
  selectedUserId: string | null;
  selectedSalesRepId: string | null;
  loading: boolean;
  error: string | null;
  // Pagination state for clients list
  clientsPagination: ClientsPaginationState;
  // Lightweight client list for dropdowns
  selectableClients: SelectableClient[];
  // Pagination state for leads kanban
  leadsPagination: LeadsPaginationState;
  fetchClients: (assignedToId?: string | null, salesRepId?: string | null) => Promise<void>;
  fetchMoreClients: () => Promise<void>;
  fetchClientsForSelect: () => Promise<void>;
  fetchLeads: (assignedToId?: string | null, salesRepId?: string | null) => Promise<void>;
  fetchClientById: (id: string, forceRefresh?: boolean) => Promise<void>;
  // asReferente: crea una persona come referente puro (stato_cliente e pipeline_id NULL),
  // l'invariante del modello Contatti/Aziende che la tiene fuori da liste lead e newsletter.
  createClient: (client: ClientFormData, options?: { skipWorkflowTrigger?: boolean; asReferente?: boolean }) => Promise<Client | null>;
  updateClient: (id: string, client: Partial<Client>, targetOrganizationId?: string) => Promise<Client | null>;
  updateSingleLead: (leadData: Client) => void;
  addSingleLead: (leadData: Client) => void;
  deleteClient: (id: string) => Promise<boolean>;
  convertLeadToClient: (id: string) => Promise<boolean>;
  setSelectedUser: (userId: string | null) => void;
  setSelectedSalesRep: (userId: string | null) => void;
  getFilteredClients: () => Client[];
  getFilteredLeads: () => Client[];
  // Pagination methods for leads kanban
  fetchLeadsByStage: (
    stageId: string,
    pipelineId: string | null,
    options?: {
      cursor?: string;
      limit?: number;
      filters?: LeadsFilters;
      reset?: boolean;
    }
  ) => Promise<void>;
  fetchMoreLeadsByStage: (stageId: string, pipelineId: string | null) => Promise<void>;
  resetLeadsPagination: () => void;
  setLeadsFilters: (filters: LeadsFilters) => void;
  initializePaginationFromExistingLeads: (stages: { id: string }[], pipelineId: string | null) => void;
  currentLeadsFilters: LeadsFilters;
  // Search function for client/lead dropdown (server-side search)
  // assignedToUserIds: optional filter to show only clients/leads assigned to specific users
  /** `limit` alza il tetto dei risultati: 50 basta agli autocomplete, le pagine Clienti
   *  e Lead ne chiedono molti di più perché poi filtrano ancora lato client. */
  searchClientsAndLeads: (searchTerm: string, assignedToUserIds?: string[], limit?: number) => Promise<Client[]>;
}

// Deduplication flags to prevent simultaneous fetches
let isFetchingLeads = false;
let isFetchingClients = false;

// Default pagination state
const defaultPaginationState: LeadsPaginationState = {
  byStage: {},
  pageSize: 50,
  initialized: false
};

// Default stage pagination state
const defaultStagePaginationState: StagePaginationState = {
  cursor: null,
  hasMore: true,
  loading: false,
  totalLoaded: 0
};

const CLIENTS_PAGE_SIZE = 200;

// Cache breve delle membership multi-pipeline: al load del kanban ogni colonna
// chiedeva le stesse righe (una query per stage). Una sola fetch per pipeline,
// filtrata client-side per stage; TTL corto per coprire solo il burst parallelo.
type PipelineMembershipRow = { client_id: string; id: string; pipeline_stage_id: string };
const pipelineMembershipsCache = new Map<string, { promise: Promise<PipelineMembershipRow[]>; ts: number }>();
const PIPELINE_MEMBERSHIPS_TTL = 4000;

function fetchPipelineMembershipsCached(pipelineId: string): Promise<PipelineMembershipRow[]> {
  const cached = pipelineMembershipsCache.get(pipelineId);
  if (cached && Date.now() - cached.ts < PIPELINE_MEMBERSHIPS_TTL) {
    return cached.promise;
  }
  const promise = (async () => {
    const { data, error } = await supabase
      .from('client_pipeline_memberships')
      .select('client_id, id, pipeline_stage_id')
      .eq('pipeline_id', pipelineId);
    if (error) throw error;
    return (data || []) as PipelineMembershipRow[];
  })();
  pipelineMembershipsCache.set(pipelineId, { promise, ts: Date.now() });
  promise.catch(() => pipelineMembershipsCache.delete(pipelineId));
  return promise;
}

// PostgREST tronca a 1000 righe: con pagine da 200 clienti le relazioni 1:N (contatti,
// deals, ecc.) possono superare il cap e perdere righe in silenzio. Spezza gli id in
// chunk e unisce i risultati.
const RELATION_CHUNK_SIZE = 50;
async function fetchRelationInChunks(
  ids: string[],
  buildQuery: (chunk: string[]) => PromiseLike<{ data: any[] | null; error: any }>
): Promise<{ data: any[] }> {
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += RELATION_CHUNK_SIZE) {
    chunks.push(ids.slice(i, i + RELATION_CHUNK_SIZE));
  }
  const results = await Promise.all(chunks.map(chunk => buildQuery(chunk)));
  return { data: results.flatMap(r => r.data || []) };
}

// Helper: fetch relations for a batch of client IDs and merge into client objects
async function _fetchAndMergeRelations(clientsData: any[], clientIds: string[], organizationId: string, clientStates: any[] | null) {
  // Collect unique FK IDs for 1:1 relations
  const assignedToIds = new Set<string>();
  const salesRepIds = new Set<string>();
  const createdByIds = new Set<string>();
  const referrerIds = new Set<string>();
  const partnerIds = new Set<string>();

  clientsData.forEach(c => {
    if (c.assigned_to) assignedToIds.add(c.assigned_to);
    if (c.sales_rep_id) salesRepIds.add(c.sales_rep_id);
    if (c.created_by) createdByIds.add(c.created_by);
    if (c.referrer_id) referrerIds.add(c.referrer_id);
    if (c.partner_id) partnerIds.add(c.partner_id);
  });

  const allUserIds = [...new Set([...assignedToIds, ...salesRepIds, ...createdByIds])];

  // Fetch all relations in parallel
  const [
    usersResult,
    referrersResult,
    partnersResult,
    contactsResult,
    locationsResult,
    dealsResult,
    projectsResult,
    activitiesResult,
    ticketsResult,
    interestsResult,
    settoriResult,
    tagsResult
  ] = await Promise.all([
    allUserIds.length > 0
      ? supabase.from('user_profiles').select('id, email, full_name, profile_image_url, phone').in('id', allUserIds)
      : { data: [] },
    referrerIds.size > 0
      ? supabase.from('referrers').select('id, full_name, email, commission_rate').in('id', [...referrerIds])
      : { data: [] },
    partnerIds.size > 0
      ? supabase.from('external_partners').select('id, name, company, email, partner_type').in('id', [...partnerIds])
      : { data: [] },
    fetchRelationInChunks(clientIds, ids => supabase.from('client_contacts').select('id, client_id, person_client_id, name, phone, email, role, area, is_primary, is_active, is_referente, is_rappresentante_legale, notes, birth_date, newsletter_subscribed, newsletter_consent_date').in('client_id', ids)),
    fetchRelationInChunks(clientIds, ids => supabase.from('client_operational_locations').select('id, client_id, name, country, address, city, province, postal_code, region, is_active, notes, is_default, recipient_name, phone, location_type').in('client_id', ids)),
    fetchRelationInChunks(clientIds, ids => supabase.from('deals').select('id, client_id, title, status, amount, deal_services(id, service_id, amount, discounted_amount, service:services(id, title, description, service_type))').in('client_id', ids)),
    fetchRelationInChunks(clientIds, ids => supabase.from('projects').select('id, client_id, name, status').in('client_id', ids)),
    // Solo pending: la UI conta solo queste (ClientList.getPendingActivities)
    fetchRelationInChunks(clientIds, ids => supabase.from('sales_activities').select('id, client_id, status, due_date').in('client_id', ids).eq('status', 'pending')),
    fetchRelationInChunks(clientIds, ids => supabase.from('tickets').select('id, client_id, status').in('client_id', ids)),
    fetchRelationInChunks(clientIds, ids => supabase.from('client_interests').select('id, client_id, interest_category_id, created_at, interest_category:interest_categories(id, name, color)').in('client_id', ids)),
    fetchRelationInChunks(clientIds, ids => supabase.from('client_settori').select('id, client_id, settore_id, created_at, settore:settori(id, name, color)').in('client_id', ids)),
    fetchRelationInChunks(clientIds, ids => supabase.from('client_tag_assignments').select('client_id, assigned_at, client_tags!inner(id, name, description, color, sort_order)').eq('organization_id', organizationId).eq('client_tags.is_active', true).in('client_id', ids)),
  ]);

  // Build lookup maps
  const userMap = new Map((usersResult.data || []).map((u: any) => [u.id, u]));
  const referrerMap = new Map((referrersResult.data || []).map((r: any) => [r.id, r]));
  const partnerMap = new Map((partnersResult.data || []).map((p: any) => [p.id, p]));
  const stateMap = new Map(clientStates?.map(state => [state.id, state]) || []);

  // Group 1:N relations by client_id
  const groupByClientId = (data: any[] | null) => {
    const map = new Map<string, any[]>();
    (data || []).forEach(item => {
      if (!map.has(item.client_id)) map.set(item.client_id, []);
      map.get(item.client_id)!.push(item);
    });
    return map;
  };

  const contactsMap = groupByClientId(contactsResult.data);
  // Referenti nuovi (client_relationships) per il read bulk — simmetrico al write.
  const referentiContactsMap = await fetchReferentiAsContacts(clientIds);
  const locationsMap = groupByClientId(locationsResult.data);
  const dealsMap = groupByClientId(dealsResult.data);
  const projectsMap = groupByClientId(projectsResult.data);
  const activitiesMap = groupByClientId(activitiesResult.data);
  const ticketsMap = groupByClientId(ticketsResult.data);
  const interestsMap = groupByClientId(interestsResult.data);
  const settoriMap = groupByClientId(settoriResult.data);

  // Tags grouped by client_id
  const tagsMap = new Map<string, any[]>();
  (tagsResult.data || []).forEach((a: any) => {
    if (!tagsMap.has(a.client_id)) tagsMap.set(a.client_id, []);
    tagsMap.get(a.client_id)!.push({ ...a.client_tags, assigned_at: a.assigned_at });
  });
  tagsMap.forEach(tags => tags.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));

  // Merge everything into enriched client objects
  return clientsData.map(client => ({
    ...client,
    assigned_user: client.assigned_to ? userMap.get(client.assigned_to) || null : null,
    sales_rep: client.sales_rep_id ? userMap.get(client.sales_rep_id) || null : null,
    creator: client.created_by ? userMap.get(client.created_by) || null : null,
    referrer: client.referrer_id ? referrerMap.get(client.referrer_id) || null : null,
    partner: client.partner_id ? partnerMap.get(client.partner_id) || null : null,
    contacts: mergeContactsByEmail(contactsMap.get(client.id) || [], referentiContactsMap[client.id] || []),
    operational_locations: locationsMap.get(client.id) || [],
    deals: dealsMap.get(client.id) || [],
    projects: projectsMap.get(client.id) || [],
    sales_activities: activitiesMap.get(client.id) || [],
    tickets: ticketsMap.get(client.id) || [],
    interests: interestsMap.get(client.id) || [],
    settori: settoriMap.get(client.id) || [],
    client_state: client.stato_cliente ? stateMap.get(client.stato_cliente) : null,
    tags: tagsMap.get(client.id) || [],
    interest_categories: (interestsMap.get(client.id) || []).map((i: any) => i.interest_category_id).filter(Boolean)
  }));
}

const defaultClientsPaginationState: ClientsPaginationState = {
  offset: 0,
  hasMore: true,
  totalCount: null,
  loading: false
};

export const useClientStore = create<ClientStore>((set, get) => ({
  clients: [],
  leads: [],
  selectedClient: null,
  selectedUserId: null,
  selectedSalesRepId: null,
  loading: false,
  error: null,
  clientsPagination: defaultClientsPaginationState,
  selectableClients: [],
  leadsPagination: defaultPaginationState,
  currentLeadsFilters: {},

  fetchClients: async (assignedToId, salesRepId) => {
    // Prevent simultaneous fetches
    if (isFetchingClients) {
      return;
    }

    isFetchingClients = true;
    set({ loading: true, error: null, clients: [], clientsPagination: { ...defaultClientsPaginationState, loading: true } });
    try {
      // Get current user's organization ID from cache
      const organizationId = await getCurrentUserOrganizationId();
      if (!organizationId) throw new Error('User not associated with an organization');

      // Fetch client states first to filter at database level
      const clientStates = await getCachedClientStates(organizationId);

      // Get IDs of states where is_lead = false (actual clients, not leads)
      const clientStateIds = clientStates?.filter(state => !state.is_lead).map(state => state.id) || [];

      // STEP 1: Fetch base client data (no joins — fast scan)
      let baseQuery = supabase
        .from('clients')
        .select('*', { count: 'exact' })
        .eq('organization_id', organizationId)
        // Fase 2: le persone-referente (individual senza stato) non sono clienti.
        // Serve anche quando l'org non ha stati configurati: in quel caso il filtro
        // per stato qui sotto non scatta e senza questa guardia i referenti
        // comparirebbero nella lista Clienti.
        .or(EXCLUDE_REFERENTI);

      if (clientStateIds.length > 0) {
        baseQuery = baseQuery.in('stato_cliente', clientStateIds);
      }
      if (assignedToId) {
        baseQuery = baseQuery.eq('assigned_to', assignedToId);
      }
      if (salesRepId) {
        baseQuery = baseQuery.eq('sales_rep_id', salesRepId);
      }

      const { data: clientsData, count: totalCount, error: clientsError } = await baseQuery
        .order('business_name', { ascending: true })
        .range(0, CLIENTS_PAGE_SIZE - 1);

      if (clientsError) throw clientsError;
      if (!clientsData || clientsData.length === 0) {
        set({ clients: [], clientsPagination: { offset: 0, hasMore: false, totalCount: totalCount ?? 0, loading: false } });
        return;
      }

      // STEP 2: Fetch all relations in parallel for the batch of client IDs
      const clientIds = clientsData.map(c => c.id);
      const enrichedClients = await _fetchAndMergeRelations(clientsData, clientIds, organizationId, clientStates);

      const hasMore = totalCount != null ? totalCount > CLIENTS_PAGE_SIZE : (enrichedClients.length === CLIENTS_PAGE_SIZE);
      set({
        clients: enrichedClients,
        clientsPagination: {
          offset: CLIENTS_PAGE_SIZE,
          hasMore,
          totalCount: totalCount ?? null,
          loading: false
        }
      });
    } catch (error) {
      set({ error: friendlyDbError(error), clientsPagination: { ...defaultClientsPaginationState, loading: false } });
    } finally {
      set({ loading: false });
      isFetchingClients = false;
    }
  },

  fetchMoreClients: async () => {
    const { clientsPagination } = get();
    if (!clientsPagination.hasMore || clientsPagination.loading) return;

    set({ clientsPagination: { ...clientsPagination, loading: true } });

    try {
      const organizationId = await getCurrentUserOrganizationId();
      if (!organizationId) return;

      const clientStates = await getCachedClientStates(organizationId);
      const clientStateIds = clientStates?.filter(state => !state.is_lead).map(state => state.id) || [];
      const { selectedUserId: assignedToId, selectedSalesRepId: salesRepId } = get();

      // STEP 1: Fetch base client data (no joins)
      let baseQuery = supabase
        .from('clients')
        .select('*')
        .eq('organization_id', organizationId);

      if (clientStateIds.length > 0) {
        baseQuery = baseQuery.in('stato_cliente', clientStateIds);
      }
      if (assignedToId) {
        baseQuery = baseQuery.eq('assigned_to', assignedToId);
      }
      if (salesRepId) {
        baseQuery = baseQuery.eq('sales_rep_id', salesRepId);
      }

      const newOffset = clientsPagination.offset;
      const { data: clientsData, error: clientsError } = await baseQuery
        .order('business_name', { ascending: true })
        .range(newOffset, newOffset + CLIENTS_PAGE_SIZE - 1);

      if (clientsError) throw clientsError;
      if (!clientsData || clientsData.length === 0) {
        set({ clientsPagination: { ...clientsPagination, hasMore: false, loading: false } });
        return;
      }

      // STEP 2: Fetch relations in parallel
      const clientIds = clientsData.map(c => c.id);
      const enrichedClients = await _fetchAndMergeRelations(clientsData, clientIds, organizationId, clientStates);

      const hasMore = enrichedClients.length === CLIENTS_PAGE_SIZE;
      set({
        clients: [...get().clients, ...enrichedClients],
        clientsPagination: {
          ...clientsPagination,
          offset: newOffset + CLIENTS_PAGE_SIZE,
          hasMore,
          loading: false
        }
      });
    } catch (error) {
      set({ clientsPagination: { ...get().clientsPagination, loading: false } });
    }
  },

  fetchClientsForSelect: async () => {
    try {
      const organizationId = await getCurrentUserOrganizationId();
      if (!organizationId) return;

      // Supabase ha limit default di 1000 righe. Paginiamo finche' la pagina
      // ritorna meno di PAGE_SIZE — copre org con decine di migliaia di clienti.
      // Stop di sicurezza a 100k per evitare runaway.
      const PAGE_SIZE = 1000;
      const MAX_TOTAL = 100_000;
      const all: Array<{ id: string; business_name: string; entity_type: string; vat_number: string | null; tax_code: string | null }> = [];
      for (let offset = 0; offset < MAX_TOTAL; offset += PAGE_SIZE) {
        const { data, error } = await supabase
          .from('clients')
          .select('id, business_name, entity_type, vat_number, tax_code')
          .eq('organization_id', organizationId)
          .or(EXCLUDE_REFERENTI) // Fase 2: no persone-referente nei selettori
          .order('business_name')
          .range(offset, offset + PAGE_SIZE - 1);
        if (error) throw error;
        const rows = data || [];
        all.push(...rows);
        if (rows.length < PAGE_SIZE) break;
      }
      set({ selectableClients: all });
    } catch {
      // silently fail
    }
  },

  fetchLeads: async (assignedToId, salesRepId) => {
    // Prevent simultaneous fetches
    if (isFetchingLeads) {
      return;
    }

    isFetchingLeads = true;
    set({ loading: true, error: null });
    try {
      // Get current user's organization ID from cache
      const organizationId = await getCurrentUserOrganizationId();
      if (!organizationId) throw new Error('User not associated with an organization');

      // Query costruita da una factory (non un builder riusato) perché va rieseguita
      // una volta per pagina nel loop di paginazione qui sotto.
      const buildLeadsQuery = () => {
        let q = supabase
        .from('clients')
        .select(`
          *,
          assigned_user:user_profiles!clients_assigned_to_fkey (
            id,
            email,
            full_name
          ),
          sales_rep:user_profiles!clients_sales_rep_id_fkey (
            id,
            email,
            full_name,
            phone
          ),
          creator:user_profiles!clients_created_by_fkey (
            id,
            email,
            full_name
          ),
          referrer:referrers!clients_referrer_id_fkey (
            id,
            full_name,
            email,
            commission_rate
          ),
          partner:external_partners!clients_partner_id_fkey (
            id,
            name,
            company,
            email,
            partner_type
          ),
          pipeline:lead_pipelines!pipeline_id (
            id,
            name,
            color
          ),
          pipeline_stage:lead_pipeline_stages!pipeline_stage_id (
            id,
            name,
            color,
            sort_order
          ),
          deals:deals (
            id,
            title,
            status,
            amount
          ),
          projects (
            id,
            name,
            status
          ),
          contacts:client_contacts!client_contacts_client_id_fkey (
            id,
            person_client_id,
            name,
            phone,
            email,
            role,
            area,
            is_primary,
            is_active,
            is_referente,
            is_rappresentante_legale,
            notes,
            birth_date,
            newsletter_subscribed,
            newsletter_consent_date
          ),
          operational_locations:client_operational_locations (
            id,
            name,
            country,
            address,
            city,
            province,
            postal_code,
            region,
            is_active,
            notes,
            is_default,
            recipient_name,
            phone,
            location_type
          ),
          interests:client_interests (
            id,
            interest_category_id,
            created_at,
            interest_category:interest_categories (
              id,
              name,
              color
            )
          ),
          settori:client_settori (
            id,
            settore_id,
            created_at,
            settore:settori (
              id,
              name,
              color
            )
          )
        `)
        .eq('organization_id', organizationId);

        // Restrizioni di visibilità del ruolo (pipeline / fonti): opzionali
        q = applyLeadVisibilityToQuery(q, usePermissionsStore.getState().getLeadVisibility());

        // Filtri separati per assegnatario e sales rep (AND logico tra di loro)
        if (assignedToId) {
          q = q.eq('assigned_to', assignedToId);
        }
        if (salesRepId) {
          q = q.eq('sales_rep_id', salesRepId);
        }
        return q;
      };

      // PostgREST tronca a max_rows (1000) di default: senza paginazione esplicita,
      // un'org con più di 1000 righe in clients perdeva silenziosamente i lead/clienti
      // più vecchi (fuori dai 1000 più recenti), introvabili anche in ricerca (report
      // AMG Educational). Pagina finché l'ultima pagina non è più piena.
      const LEADS_FETCH_PAGE_SIZE = 900;
      let clientsData: any[] = [];
      let offset = 0;
      while (true) {
        const { data: page, error: pageError } = await buildLeadsQuery()
          .order('created_at', { ascending: false })
          .range(offset, offset + LEADS_FETCH_PAGE_SIZE - 1);
        if (pageError) throw pageError;
        clientsData = clientsData.concat(page || []);
        if (!page || page.length < LEADS_FETCH_PAGE_SIZE) break;
        offset += LEADS_FETCH_PAGE_SIZE;
      }

      // Fetch client states from cache
      const clientStates = await getCachedClientStates(organizationId);

      // Fetch client tags with assignments
      let tagAssignments = [];
      try {
        const { data: tagsData, error: tagError } = await supabase
          .from('client_tag_assignments')
          .select(`
            client_id,
            assigned_at,
            client_tags!inner (
              id,
              name,
              description,
              color,
              sort_order
            )
          `)
          .eq('organization_id', organizationId)
          .eq('client_tags.is_active', true);

        if (!tagError) {
          tagAssignments = tagsData || [];
        }
      } catch (error) {
        // Tags table not available, continue without tags
      }

      // Create maps for efficient lookup
      const stateMap = new Map(clientStates?.map(state => [state.id, state]) || []);
      const tagsMap = new Map();

      // Group tags by client_id and sort them
      tagAssignments?.forEach(assignment => {
        if (!tagsMap.has(assignment.client_id)) {
          tagsMap.set(assignment.client_id, []);
        }
        tagsMap.get(assignment.client_id).push({ ...assignment.client_tags, assigned_at: assignment.assigned_at });
      });

      // Sort tags within each client by sort_order
      tagsMap.forEach((tags, clientId) => {
        tags.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      });

      // Add client_state, tags, and interest_categories to each client
      const enrichedClients = clientsData?.map(client => ({
        ...client,
        client_state: client.stato_cliente ? stateMap.get(client.stato_cliente) : null,
        tags: tagsMap.get(client.id) || [],
        interest_categories: (client as any).interests?.map((i: any) => i.interest_category_id).filter(Boolean) || []
      })) || [];

      // Filtra per includere solo i clienti con stati marcati come leads
      const filteredLeads = enrichedClients.filter(client => client.client_state?.is_lead);

      set({ leads: filteredLeads });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
      isFetchingLeads = false;
    }
  },

  fetchClientById: async (id: string, forceRefresh = false) => {
    // Check if we already have this client and don't need to force refresh
    if (!forceRefresh && get().selectedClient && get().selectedClient.id === id) {
      return;
    }

    set({ loading: true, error: null });
    try {
      // Get current user's organization ID from cache
      const organizationId = await getCurrentUserOrganizationId();

      const { data: clientData, error: clientError } = await supabase
        .from('clients')
        .select(`
          *,
          assigned_user:user_profiles!clients_assigned_to_fkey (
            id,
            email,
            full_name
          ),
          sales_rep:user_profiles!clients_sales_rep_id_fkey (
            id,
            email,
            full_name,
            phone
          ),
          creator:user_profiles!clients_created_by_fkey (
            id,
            email,
            full_name
          ),
          referrer:referrers!clients_referrer_id_fkey (
            id,
            full_name,
            email,
            commission_rate
          ),
          partner:external_partners!clients_partner_id_fkey (
            id,
            name,
            company,
            email,
            partner_type
          ),
          contacts:client_contacts!client_contacts_client_id_fkey (
            id,
            person_client_id,
            name,
            phone,
            email,
            role,
            area,
            is_primary,
            is_active,
            is_referente,
            is_rappresentante_legale,
            notes,
            birth_date,
            newsletter_subscribed,
            newsletter_consent_date
          ),
          operational_locations:client_operational_locations (
            id,
            name,
            country,
            address,
            city,
            province,
            postal_code,
            region,
            is_active,
            notes,
            is_default,
            recipient_name,
            phone,
            location_type
          ),
          interests:client_interests (
            id,
            interest_category_id,
            product_id,
            service_id,
            notes,
            priority,
            status,
            created_at,
            interest_category:interest_categories (
              id,
              name,
              description,
              category_type,
              color
            )
          ),
          settori:client_settori (
            id,
            settore_id,
            created_at,
            settore:settori (
              id,
              name,
              color
            )
          ),
          tag_assignments:client_tag_assignments (
            id,
            assigned_at,
            tag:client_tags (
              id,
              name,
              description,
              color,
              sort_order
            )
          ),
          custom_fields:client_custom_fields (
            id,
            field_definition_id,
            field_value,
            field_definition:custom_field_definitions (
              id,
              field_name,
              field_label,
              field_type,
              field_options
            )
          )
        `)
        .eq('id', id)
        .eq('organization_id', organizationId)
        .single();

      if (clientError) throw clientError;

      console.log('🔍 Dati client caricati da fetchClientById:', {
        website: clientData.website,
        trade_name: clientData.trade_name,
        lead_received_date: clientData.lead_received_date,
        spoken_languages: clientData.spoken_languages,
        continent: clientData.continent,
        country: clientData.country,
        macro_area: clientData.macro_area,
        micro_area: clientData.micro_area,
        lead_source_details: clientData.lead_source_details,
        lead_source_url: clientData.lead_source_url,
        newsletter_subscribed: clientData.newsletter_subscribed,
        newsletter_email: clientData.newsletter_email,
        general_comments: clientData.general_comments
      });

      // Process nested data from JOIN queries
      const tags = clientData.tag_assignments?.map(assignment =>
          assignment.tag ? { ...assignment.tag, assigned_at: assignment.assigned_at } : null
        )
        .filter(tag => tag) // Remove null tags
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)) || [];

      const legacyContacts = clientData.contacts?.filter(contact => contact.is_active) || [];
      // Read simmetrico al write: unisce i referenti nuovi (client_relationships) ai
      // client_contacts legacy, così il form di modifica li mostra e il diff soft-unlink
      // di updateClient non li scollega (vedi fetchReferentiAsContacts / bug §14).
      const { fetchReferentiAsContacts, mergeContactsByEmail } = await import('../utils/fetchReferentiContacts');
      const referentiMap = await fetchReferentiAsContacts([id]);
      const contacts = mergeContactsByEmail(legacyContacts, referentiMap[id] || []);

      const interests = clientData.interests || [];

      // Fetch static reference data from cache
      const [clientStates, leadSources] = await Promise.all([
        getCachedClientStates(organizationId),
        getCachedLeadSources(organizationId)
      ]);

      // Create maps for efficient lookup
      const stateMap = new Map(clientStates?.map(state => [state.id, state]) || []);
      const leadSourceMap = new Map(leadSources?.map(source => [source.id, source]) || []);

      // Build the enriched client with all data
      const mappedInterestCategories = interests?.map((i: any) => i.interest_category_id).filter(Boolean) || [];

      console.log('[ClientStore fetchClientById] Interessi caricati:', {
        clientId: id,
        businessName: clientData.business_name,
        interests: interests,
        mappedInterestCategories: mappedInterestCategories
      });

      const enrichedClient = {
        ...clientData,
        client_state: clientData.stato_cliente ? stateMap.get(clientData.stato_cliente) : null,
        lead_source_data: clientData.lead_source ? leadSourceMap.get(clientData.lead_source) : null,
        tags,
        contacts,
        interests,
        // Map interests to interest_categories (array of IDs) for form compatibility
        interest_categories: mappedInterestCategories
      };

      console.log('[ClientStore fetchClientById] enrichedClient creato con interest_categories:', enrichedClient.interest_categories);

      // Restrizioni di visibilità del ruolo: il lead fuori dalle pipeline/fonti
      // consentite non si apre nemmeno per URL diretto.
      if (!isLeadVisible(enrichedClient, usePermissionsStore.getState().getLeadVisibility())) {
        set({ selectedClient: null, error: LEAD_NOT_VISIBLE_MESSAGE });
        return;
      }

      set({ selectedClient: enrichedClient });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  createClient: async (client: ClientFormData, options?: { skipWorkflowTrigger?: boolean; asReferente?: boolean }) => {
    try {
      // Get current authenticated user for created_by tracking
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      // Get organization ID - use provided one or fallback to current user's
      let targetOrganizationId = client.organization_id;

      if (!targetOrganizationId) {
        // Fallback to current user's organization ID
        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('organization_id')
          .eq('id', userData.user.id)
          .single();

        if (!userProfile?.organization_id) throw new Error('User not associated with an organization');
        targetOrganizationId = userProfile.organization_id;
      }

      // Auto-assign stato_cliente if not provided (align with mobile).
      // Eccezione: i referenti puri devono restare senza stato e senza pipeline,
      // altrimenti comparirebbero nelle liste lead come doppioni dell'azienda.
      let statoCliente = client.stato_cliente;
      if (!statoCliente && !options?.asReferente) {
        const clientStates = await getCachedClientStates(targetOrganizationId);
        const leadStates = clientStates?.filter(s => s.is_lead) || [];
        statoCliente = leadStates[0]?.id || null;
      }
      if (options?.asReferente) statoCliente = null;

      // Un lead senza pipeline non compare nel kanban, che mostra i lead per fase: se
      // chi crea non ne ha scelta una, lo mettiamo nella pipeline predefinita, prima
      // fase. Vale solo per i lead veri (i referenti restano fuori di proposito) e solo
      // per le organizzazioni che le pipeline le usano.
      let pipelineId = options?.asReferente ? null : (client.pipeline_id || null);
      let pipelineStageId = options?.asReferente ? null : (client.pipeline_stage_id || null);
      if (!pipelineId && !options?.asReferente && statoCliente) {
        const clientStates = await getCachedClientStates(targetOrganizationId);
        const isLeadState = clientStates?.some(s => s.id === statoCliente && s.is_lead);
        if (isLeadState) {
          const fallback = await resolveDefaultPipelineForOrg(targetOrganizationId);
          if (fallback) {
            pipelineId = fallback.pipeline_id;
            pipelineStageId = fallback.pipeline_stage_id;
          }
        }
      }

      const insertData = {
          entity_type: client.entity_type,
          client_code: client.client_code || null,
          business_name: client.business_name,
          birth_date: client.birth_date || null,
          vat_number: client.vat_number || null,
          tax_code: client.tax_code || null,
          sdi_code: client.sdi_code || null,
          ateco_code: client.ateco_code || null,
          chamber_of_commerce: client.chamber_of_commerce || null,
          street_address: client.street_address || null,
          street_number: client.street_number || null,
          postal_code: client.postal_code || null,
          city: client.city || null,
          province: client.province || null,
          phone: client.phone || null,
          email: client.email || null,
          pec: client.pec || null,
          whatsapp_number: client.whatsapp_number || null,
          // custom_fields managed separately via client_custom_fields table
          assigned_to: client.assigned_to || userData.user.id,
          sales_rep_id: client.sales_rep_id || null,
          referrer_id: client.referrer_id || null,
          partner_id: client.partner_id || null,
          accounting_regime: client.accounting_regime || null,
          client_category: client.client_category || null,
          country_code: client.country_code || '+39',
          default_payment_method_id: client.default_payment_method_id || null,
          default_price_list_id: client.default_price_list_id || null,
          // Aliquota IVA del cliente: solo se il form la gestisce (colonna presente)
          ...((client as { default_vat_rate?: number | null }).default_vat_rate !== undefined
            ? { default_vat_rate: (client as { default_vat_rate?: number | null }).default_vat_rate ?? null }
            : {}),
          // Codice IVA del cliente: solo se il form lo gestisce (funzione attiva e colonna presente)
          ...(client.vat_code_id !== undefined ? { vat_code_id: client.vat_code_id || null } : {}),
          lead_source: client.lead_source || null,
          lead_source_details: client.lead_source_details || null,
          lead_source_url: client.lead_source_url || null,
          lead_stage: client.lead_stage || null,
          newsletter_subscribed: client.newsletter_subscribed || false,
          newsletter_email: client.newsletter_email || null,
          region: client.region || null,
          email_amministrazione: client.email_amministrazione || null,
          email_legale_rappresentante: client.email_legale_rappresentante || null,
          iban: client.iban || null,
          abi: client.abi || null,
          cab: client.cab || null,
          stato_cliente: statoCliente,
          // Advanced fields from migration
          website: client.website || null,
          ...socialPayload(client),
          logo_url: client.logo_url || null,
          trade_name: client.trade_name || null,
          lead_received_date: client.lead_received_date || null,
          spoken_languages: client.spoken_languages || null,
          continent: client.continent || null,
          country: client.country || null,
          country_iso: isoFromCountryName(client.country),
          macro_area: client.macro_area || null,
          micro_area: client.micro_area || null,
          initial_agent_id: client.initial_agent_id || null,
          assigned_agent_id: client.assigned_agent_id || null,
          general_comments: client.general_comments || null,
          organization_id: targetOrganizationId,
          created_by: userData.user.id, // Track who created this lead/client
          // Marketing consent fields from migration
          marketing_consent_email: client.marketing_consent_email || false,
          marketing_consent_sms: client.marketing_consent_sms || false,
          marketing_consent_phone: client.marketing_consent_phone || false,
          marketing_notes: client.marketing_notes || null,
          marketing_consent_date: client.marketing_consent_date || null,
          interest_notes: client.interest_notes || null,
          // Operational address fields from migration
          has_operational_address: client.has_operational_address || false,
          operational_country: client.operational_country || null,
          operational_country_iso: isoFromCountryName(client.operational_country),
          operational_address: client.operational_address || null,
          operational_city: client.operational_city || null,
          operational_province: client.operational_province || null,
          operational_postal_code: client.operational_postal_code || null,
          operational_region: client.operational_region || null,
          // Pipeline fields
          pipeline_id: pipelineId,
          pipeline_stage_id: pipelineStageId,
          // Score and opportunity value
          lead_score: client.score || client.lead_score || null,
          opportunity_value: client.opportunity_value || null,
          // UTM tracking and ad attribution
          utm_source: client.utm_source || null,
          utm_medium: client.utm_medium || null,
          utm_campaign: client.utm_campaign || null,
          utm_content: client.utm_content || null,
          utm_term: client.utm_term || null,
          fbclid: client.fbclid || null,
          gclid: client.gclid || null,
          ad_platform: client.ad_platform || null,
          ad_campaign_id: client.ad_campaign_id || null,
          ad_adset_id: client.ad_adset_id || null,
          ad_ad_id: client.ad_ad_id || null,
          ad_ad_name: client.ad_ad_name || null,
          ad_adset_name: client.ad_adset_name || null,
          ad_attribution_method: client.ad_attribution_method || null,
          ad_attributed_at: client.ad_attributed_at || null,
        };

      const { data, error } = await supabase
        .from('clients')
        .insert(insertData)
        .select(`
          *,
          assigned_user:user_profiles!clients_assigned_to_fkey (
            id,
            email,
            full_name
          ),
          sales_rep:user_profiles!clients_sales_rep_id_fkey (
            id,
            email,
            full_name,
            phone
          ),
          creator:user_profiles!clients_created_by_fkey (
            id,
            email,
            full_name
          )
        `)
        .single();

      if (error) throw error;

      // 1. Referenti come persone reali + client_relationships (modello Contatti/Aziende first-class,
      //    sostituisce la scrittura legacy su client_contacts)
      if (client.contacts && client.contacts.length > 0) {
        try {
          const { persistClientReferenti } = await import('../utils/persistClientContacts');
          await persistClientReferenti(data.id, targetOrganizationId, client.contacts);
        } catch (contactError) {
          console.error('❌ Failed to persist client referenti:', contactError);
          // Don't fail the entire creation for contact assignment errors
        }
      }

      // 1b. Handle operational locations if provided
      if (client.operational_locations && client.operational_locations.length > 0) {
        try {
          const locationsToInsert = client.operational_locations.map(loc => ({
            client_id: data.id,
            organization_id: targetOrganizationId,
            name: loc.name,
            country: loc.country || null,
            country_iso: isoFromCountryName(loc.country),
            address: loc.address || null,
            city: loc.city || null,
            province: loc.province || null,
            postal_code: loc.postal_code || null,
            region: loc.region || null,
            is_active: true,
            notes: loc.notes || null,
            is_default: !!loc.is_default,
            recipient_name: loc.recipient_name || null,
            phone: loc.phone || null,
            location_type: loc.location_type || 'operational'
          }));

          const { error: locationsError } = await supabase
            .from('client_operational_locations')
            .insert(locationsToInsert);

          if (locationsError) {
            console.error('❌ Failed to save operational locations:', locationsError);
          }
        } catch (locationError) {
          // Don't fail the entire creation for operational location errors
        }
      }

      // 2. Handle interest category assignments if provided (align with mobile)
      if (client.selected_interest_categories && client.selected_interest_categories.length > 0) {
        try {
          const interestsToInsert = client.selected_interest_categories.map(categoryId => ({
            client_id: data.id,
            organization_id: targetOrganizationId,
            interest_category_id: categoryId
          }));

          const { error: interestsError } = await supabase
            .from('client_interests')
            .insert(interestsToInsert);

          if (interestsError) {
            console.error('❌ Failed to save client interest categories:', interestsError);
          }
        } catch (interestError) {
          // Don't fail the entire creation for interest category assignment errors
        }
      }

      // 3. Handle tag assignments if provided (align with mobile)
      if (client.selected_tags && client.selected_tags.length > 0) {
        try {
          const tagStore = useClientTagStore.getState();
          await tagStore.assignTagsToClient(data.id, client.selected_tags);
        } catch (tagError) {
          // Don't fail the entire creation for tag assignment errors
        }
      }

      // 4. Handle settori assignments if provided
      if (client.selected_settori && client.selected_settori.length > 0) {
        try {
          const { useSettoriStore } = await import('./settoriStore');
          await useSettoriStore.getState().assignSettoriToClient(data.id, client.selected_settori);
        } catch (settoriError) {
          // Don't fail the entire creation for settori assignment errors
        }
      }

      // Determine if this is a lead using the cached client states (same pattern used elsewhere)
      let isLead = false;
      try {
        const cachedStates = await getCachedClientStates(targetOrganizationId);
        const stateInfo = cachedStates?.find(s => s.id === data.stato_cliente);
        isLead = stateInfo?.is_lead ?? false;
        console.log('[CLIENT STORE] createClient - isLead check:', {
          stato_cliente: data.stato_cliente,
          is_lead_parsed: isLead
        });
      } catch (error) {
        console.error('[CLIENT STORE] createClient - Error determining client state:', error);
        // Could not determine client state, assume not a lead
      }

      // Fase 2: una persona-referente (individual senza stato) non appartiene ne' ai lead
      // ne' ai clienti. Le query di fetch la escludono gia' (EXCLUDE_REFERENTI): senza questo
      // guard comparirebbe nella lista Clienti fino al primo ricaricamento.
      const isReferentePuro = data.entity_type === 'individual' && !data.stato_cliente;

      // Update the appropriate array based on whether it's a lead or a client
      if (isReferentePuro) {
        // nessun array da aggiornare
      } else if (isLead) {
        const leads = get().leads;
        console.log('[CLIENT STORE] createClient - Adding to LEADS array:', {
          newLeadId: data.id,
          newLeadName: data.business_name,
          pipeline_id: data.pipeline_id,
          pipeline_stage_id: data.pipeline_stage_id,
          currentLeadsCount: leads.length
        });
        // Mantieni allineato il contatore per-stage del Kanban (mirror del decremento in deleteClient):
        // il totalCount viene calcolato solo al reset, quindi va incrementato qui per i lead creati in sessione.
        const stageId = getEffectiveStageId(data);
        const currentPagination = get().leadsPagination;
        const stagePag = stageId ? currentPagination.byStage[stageId] : undefined;
        const updatedPagination = (stageId && stagePag)
          ? {
              ...currentPagination,
              byStage: {
                ...currentPagination.byStage,
                [stageId]: {
                  ...stagePag,
                  totalCount: (stagePag.totalCount ?? 0) + 1,
                  totalLoaded: (stagePag.totalLoaded ?? 0) + 1
                }
              }
            }
          : currentPagination;
        set({ leads: [...leads, data], leadsPagination: updatedPagination });
      } else {
        const clients = get().clients;
        console.log('[CLIENT STORE] createClient - Adding to CLIENTS array:', {
          newClientId: data.id,
          newClientName: data.business_name,
          currentClientsCount: clients.length
        });
        set({ clients: [...clients, data] });
      }

      // Emit event for workflow triggers (skip if called from deal/CSV creation)
      // Fase 2: mai scatenare workflow per una persona-referente (individual senza stato).
      if (!options?.skipWorkflowTrigger && !isReferentePuro) {
        try {
          if (isLead) {
            console.log('🔄 [CLIENT STORE] Emitting lead:created event');
            eventBus.emitEvent({
              type: 'lead:created',
              payload: {
                leadId: data.id,
                pipelineId: data.pipeline_id,
                pipelineStageId: data.pipeline_stage_id,
                leadSource: data.lead_source,
                adPlatform: data.ad_platform,
                businessName: data.business_name,
                contactName: data.contact_name,
                email: data.email,
                phone: data.phone,
                statoCliente: data.stato_cliente,
                assignedTo: data.assigned_to,
                interestCategoryIds: client.selected_interest_categories
              }
            });
          } else {
            console.log('🔄 [CLIENT STORE] Emitting client:created event');
            eventBus.emitEvent({
              type: 'client:created',
              payload: {
                clientId: data.id,
                businessName: data.business_name,
                statoCliente: data.stato_cliente
              }
            });
          }
        } catch (eventError) {
          console.error('Failed to emit event:', eventError);
          // Don't fail the creation if event fails
        }
      } else {
        console.log('🔄 [CLIENT STORE] Skipping workflow trigger (skipWorkflowTrigger=true)');
      }

      // Sync client to newsletter lists (async, non-blocking)
      // This creates records in newsletter_subscribers for matching lists,
      // which triggers the database trigger to invoke workflow edge functions
      try {
        const orgId = data.organization_id || targetOrganizationId;
        if (orgId) {
          newsletterListSyncService.syncClientToListsAsync(data.id, orgId, isLead);
        }
      } catch (syncError) {
        console.error('[CLIENT STORE] Newsletter sync error (non-blocking):', syncError);
      }

      // Enrich lead via VIES VAT validation + website scraping (async, non-blocking)
      if (data.vat_number || data.website) {
        try {
          supabase.functions.invoke('lead-enrich', {
            body: { client_id: data.id, organization_id: data.organization_id || targetOrganizationId },
          }).then(({ data: enrichResult, error: enrichError }) => {
            if (enrichError) {
              console.error('[CLIENT STORE] VIES enrich error (non-blocking):', enrichError);
              return;
            }
            if (enrichResult?.enriched && enrichResult.fields_updated?.length > 0) {
              // Refresh the client/lead in the store with enriched data
              const updatedFields: Record<string, any> = {};
              if (enrichResult.fields_updated.includes('business_name') && enrichResult.vies_name) {
                updatedFields.business_name = enrichResult.vies_name;
              }
              // Re-fetch the full client to get all updated fields
              supabase
                .from('clients')
                .select('*')
                .eq('id', data.id)
                .single()
                .then(({ data: refreshed }) => {
                  if (refreshed) {
                    const currentLeads = get().leads;
                    const currentClients = get().clients;
                    if (isLead) {
                      set({ leads: currentLeads.map(l => l.id === data.id ? { ...l, ...refreshed } : l) });
                    } else {
                      set({ clients: currentClients.map(c => c.id === data.id ? { ...c, ...refreshed } : c) });
                    }
                  }
                });
            }
          }).catch(err => console.error('[CLIENT STORE] VIES enrich error (non-blocking):', err));
        } catch (enrichErr) {
          console.error('[CLIENT STORE] VIES enrich import error (non-blocking):', enrichErr);
        }
      }

      // Auto-fetch company logo from domain (async, non-blocking)
      if (!data.logo_url) {
        try {
          const { getClientDomain, fetchBestLogoUrl } = await import('../utils/companyLogoService');
          const domain = getClientDomain(data.email, data.website);
          if (domain) {
            fetchBestLogoUrl(domain).then(async (logoUrl) => {
              if (logoUrl) {
                const { error: logoError } = await supabase
                  .from('clients')
                  .update({ logo_url: logoUrl })
                  .eq('id', data.id);
                if (!logoError) {
                  const currentClients = get().clients;
                  const currentLeads = get().leads;
                  if (isLead) {
                    set({ leads: currentLeads.map(l => l.id === data.id ? { ...l, logo_url: logoUrl } : l) });
                  } else {
                    set({ clients: currentClients.map(c => c.id === data.id ? { ...c, logo_url: logoUrl } : c) });
                  }
                }
              }
            }).catch(err => console.error('[CLIENT STORE] Logo fetch error (non-blocking):', err));
          }
        } catch (logoError) {
          console.error('[CLIENT STORE] Logo service import error (non-blocking):', logoError);
        }
      }

      // Geocodifica in background (non blocca il salvataggio)
      if ((insertData.city || insertData.country) && !insertData.latitude) {
        geocodeAddress({
          street_address: insertData.street_address,
          street_number: insertData.street_number,
          city: insertData.city,
          province: insertData.province,
          postal_code: insertData.postal_code,
          country: insertData.country,
        }).then(geo => {
          if (geo) {
            supabase.from('clients').update({
              latitude: geo.latitude,
              longitude: geo.longitude,
              geocoded_at: geo.geocoded_at,
            }).eq('id', data.id).then(() => {
              set(state => ({
                clients: state.clients.map(c => c.id === data.id ? { ...c, ...geo } : c),
                leads: state.leads.map(l => l.id === data.id ? { ...l, ...geo } : l),
              }));
            });
          }
        }).catch(() => {});
      }

      // Notify onboarding checklist when a lead is created manually
      if (isLead && !options?.skipWorkflowTrigger) {
        dispatchTaskCompleted('lead', 'client_store_create');
      }

      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  updateClient: async (id: string, client: Partial<Client>, targetOrganizationId?: string) => {
    try {
      // Use targetOrganizationId if provided, otherwise get current user's organization ID
      let organizationId = targetOrganizationId;
      let userProfile = null;

      if (!organizationId) {
        // Fallback to current user's organization ID for backward compatibility
        const { data: userData } = await supabase.auth.getUser();
        if (!userData.user) throw new Error('User not authenticated');

        const { data: profile } = await supabase
          .from('user_profiles')
          .select('organization_id')
          .eq('id', userData.user.id)
          .single();

        if (!profile?.organization_id) throw new Error('User not associated with an organization');
        organizationId = profile.organization_id;
        userProfile = profile;
      } else {
        // Still get user profile for some operations that need it
        const { data: userData } = await supabase.auth.getUser();
        if (userData.user) {
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('organization_id')
            .eq('id', userData.user.id)
            .single();
          userProfile = profile;
        }
      }

      // Fetch the current client to check if status is changing
      const { data: currentClient, error: fetchError } = await supabase
        .from('clients')
        .select('id, stato_cliente, lead_stage, assigned_to, lead_source, business_name, organization_id, pipeline_id, pipeline_stage_id, newsletter_subscribed, email, website, logo_url')
        .eq('id', id)
        .eq('organization_id', organizationId)
        .maybeSingle();

      if (fetchError) {
        console.error('Error fetching current client:', fetchError);
        throw new Error(`Could not fetch client: ${fetchError.message}`);
      }

      if (!currentClient) {
        throw new Error('Client not found in database');
      }

      const oldStatusId = currentClient?.stato_cliente;
      const newStatusId = client.stato_cliente;
      const oldLeadStage = currentClient?.lead_stage;
      const newLeadStage = client.lead_stage;

      // Prepare update data, only including fields that are explicitly provided
      const updateData: Record<string, any> = {};
      
      // Only update fields that are explicitly provided
      if (client.entity_type !== undefined) updateData.entity_type = client.entity_type;
      if (client.client_code !== undefined) updateData.client_code = client.client_code || null;
      if (client.business_name !== undefined) updateData.business_name = client.business_name;
      if (client.birth_date !== undefined) updateData.birth_date = client.birth_date || null;
      if (client.vat_number !== undefined) updateData.vat_number = client.vat_number || null;
      if (client.tax_code !== undefined) updateData.tax_code = client.tax_code || null;
      if (client.sdi_code !== undefined) updateData.sdi_code = client.sdi_code || null;
      if (client.ateco_code !== undefined) updateData.ateco_code = client.ateco_code || null;
      if (client.chamber_of_commerce !== undefined) updateData.chamber_of_commerce = client.chamber_of_commerce || null;
      if (client.street_address !== undefined) updateData.street_address = client.street_address || null;
      if (client.street_number !== undefined) updateData.street_number = client.street_number || null;
      if (client.postal_code !== undefined) updateData.postal_code = client.postal_code || null;
      if (client.city !== undefined) updateData.city = client.city || null;
      if (client.province !== undefined) updateData.province = client.province || null;
      if (client.phone !== undefined) updateData.phone = client.phone || null;
      if (client.email !== undefined) updateData.email = client.email || null;
      if (client.pec !== undefined) updateData.pec = client.pec || null;
      if (client.whatsapp_number !== undefined) updateData.whatsapp_number = client.whatsapp_number || null;
      // custom_fields managed separately via client_custom_fields table
      if (client.assigned_to !== undefined) updateData.assigned_to = client.assigned_to || null;
      if (client.accounting_regime !== undefined) updateData.accounting_regime = client.accounting_regime || null;
      if (client.sales_rep_id !== undefined) updateData.sales_rep_id = client.sales_rep_id || null;
      if (client.referrer_id !== undefined) updateData.referrer_id = client.referrer_id || null;
      if (client.partner_id !== undefined) updateData.partner_id = client.partner_id || null;
      if (client.stato_cliente !== undefined) updateData.stato_cliente = client.stato_cliente || null;
      if (client.lead_stage !== undefined) updateData.lead_stage = client.lead_stage || null;
      if (client.pipeline_id !== undefined) updateData.pipeline_id = client.pipeline_id || null;
      if (client.pipeline_stage_id !== undefined) updateData.pipeline_stage_id = client.pipeline_stage_id || null;
      if (client.lead_score !== undefined) updateData.lead_score = client.lead_score ?? null;
      if (client.opportunity_value !== undefined) updateData.opportunity_value = client.opportunity_value ?? null;
      if (client.last_activity_date !== undefined) updateData.last_activity_date = client.last_activity_date || null;
      // Motivo della perdita: prima veniva scartato qui, e il motivo scelto nel drawer del
      // Lead (fase persa) non arrivava mai al DB.
      if ((client as any).lost_reason !== undefined) updateData.lost_reason = (client as any).lost_reason || null;
      if ((client as any).lost_reason_date !== undefined) updateData.lost_reason_date = (client as any).lost_reason_date || null;
      if (client.lead_source !== undefined) updateData.lead_source = client.lead_source || null;
      if (client.lead_source_details !== undefined) updateData.lead_source_details = client.lead_source_details || null;
      if (client.lead_source_url !== undefined) updateData.lead_source_url = client.lead_source_url || null;
      if (client.newsletter_subscribed !== undefined) updateData.newsletter_subscribed = client.newsletter_subscribed;
      if (client.newsletter_email !== undefined) updateData.newsletter_email = client.newsletter_email || null;
      if (client.region !== undefined) updateData.region = client.region || null;
      if (client.client_category !== undefined) updateData.client_category = client.client_category || null;
      if (client.country_code !== undefined) updateData.country_code = client.country_code || '+39';
      if (client.default_payment_method_id !== undefined) updateData.default_payment_method_id = client.default_payment_method_id || null;
      if (client.default_price_list_id !== undefined) updateData.default_price_list_id = client.default_price_list_id || null;
      if (client.default_vat_rate !== undefined) updateData.default_vat_rate = client.default_vat_rate ?? null;
      if (client.vat_code_id !== undefined) updateData.vat_code_id = client.vat_code_id || null;
      if (client.email_amministrazione !== undefined) updateData.email_amministrazione = client.email_amministrazione || null;
      if (client.email_legale_rappresentante !== undefined) updateData.email_legale_rappresentante = client.email_legale_rappresentante || null;
      if (client.iban !== undefined) updateData.iban = client.iban || null;
      if (client.abi !== undefined) updateData.abi = client.abi || null;
      if (client.cab !== undefined) updateData.cab = client.cab || null;
      // Advanced fields from migration
      if (client.website !== undefined) updateData.website = client.website || null;
      Object.assign(updateData, socialPayload(client));
      if (client.logo_url !== undefined) updateData.logo_url = client.logo_url || null;
      if (client.trade_name !== undefined) updateData.trade_name = client.trade_name || null;
      if (client.lead_received_date !== undefined) updateData.lead_received_date = client.lead_received_date || null;
      if (client.spoken_languages !== undefined) updateData.spoken_languages = client.spoken_languages || null;
      if (client.continent !== undefined) updateData.continent = client.continent || null;
      if (client.country !== undefined) {
        updateData.country = client.country || null;
        updateData.country_iso = isoFromCountryName(client.country);
      }
      if (client.macro_area !== undefined) updateData.macro_area = client.macro_area || null;
      if (client.micro_area !== undefined) updateData.micro_area = client.micro_area || null;
      // Geocoding fields
      if (client.latitude !== undefined) updateData.latitude = client.latitude;
      if (client.longitude !== undefined) updateData.longitude = client.longitude;
      if (client.geocoded_at !== undefined) updateData.geocoded_at = client.geocoded_at;
      if (client.initial_agent_id !== undefined) updateData.initial_agent_id = client.initial_agent_id || null;
      if (client.assigned_agent_id !== undefined) updateData.assigned_agent_id = client.assigned_agent_id || null;
      if (client.general_comments !== undefined) updateData.general_comments = client.general_comments || null;
      // Marketing consent fields from migration
      if (client.marketing_consent_email !== undefined) updateData.marketing_consent_email = client.marketing_consent_email || false;
      if (client.marketing_consent_sms !== undefined) updateData.marketing_consent_sms = client.marketing_consent_sms || false;
      if (client.marketing_consent_phone !== undefined) updateData.marketing_consent_phone = client.marketing_consent_phone || false;
      if (client.marketing_notes !== undefined) updateData.marketing_notes = client.marketing_notes || null;
      if (client.marketing_consent_date !== undefined) updateData.marketing_consent_date = client.marketing_consent_date || null;
      if (client.interest_notes !== undefined) updateData.interest_notes = client.interest_notes || null;
      // Operational address fields from migration
      if (client.has_operational_address !== undefined) updateData.has_operational_address = client.has_operational_address || false;
      if (client.operational_country !== undefined) {
        updateData.operational_country = client.operational_country || null;
        updateData.operational_country_iso = isoFromCountryName(client.operational_country);
      }
      if (client.operational_address !== undefined) updateData.operational_address = client.operational_address || null;
      if (client.operational_city !== undefined) updateData.operational_city = client.operational_city || null;
      if (client.operational_province !== undefined) updateData.operational_province = client.operational_province || null;
      if (client.operational_postal_code !== undefined) updateData.operational_postal_code = client.operational_postal_code || null;
      if (client.operational_region !== undefined) updateData.operational_region = client.operational_region || null;
      // Pipeline fields
      if (client.pipeline_id !== undefined) updateData.pipeline_id = client.pipeline_id || null;
      if (client.pipeline_stage_id !== undefined) updateData.pipeline_stage_id = client.pipeline_stage_id || null;
      // UTM tracking and ad attribution
      if (client.utm_source !== undefined) updateData.utm_source = client.utm_source || null;
      if (client.utm_medium !== undefined) updateData.utm_medium = client.utm_medium || null;
      if (client.utm_campaign !== undefined) updateData.utm_campaign = client.utm_campaign || null;
      if (client.utm_content !== undefined) updateData.utm_content = client.utm_content || null;
      if (client.utm_term !== undefined) updateData.utm_term = client.utm_term || null;
      if (client.fbclid !== undefined) updateData.fbclid = client.fbclid || null;
      if (client.gclid !== undefined) updateData.gclid = client.gclid || null;
      if (client.ad_platform !== undefined) updateData.ad_platform = client.ad_platform || null;
      if (client.ad_campaign_id !== undefined) updateData.ad_campaign_id = client.ad_campaign_id || null;
      if (client.ad_adset_id !== undefined) updateData.ad_adset_id = client.ad_adset_id || null;
      if (client.ad_ad_id !== undefined) updateData.ad_ad_id = client.ad_ad_id || null;
      if (client.ad_ad_name !== undefined) updateData.ad_ad_name = client.ad_ad_name || null;
      if (client.ad_adset_name !== undefined) updateData.ad_adset_name = client.ad_adset_name || null;
      if (client.ad_attribution_method !== undefined) updateData.ad_attribution_method = client.ad_attribution_method || null;
      if (client.ad_attributed_at !== undefined) updateData.ad_attributed_at = client.ad_attributed_at || null;

      // Track pipeline change for workflow trigger
      const oldPipelineId = currentClient.pipeline_id;
      const newPipelineId = client.pipeline_id;

      // First check if the client exists and belongs to this organization
      const { data: existingClient, error: checkError } = await supabase
        .from('clients')
        .select('id, organization_id')
        .eq('id', id)
        .maybeSingle();

      if (checkError) {
        console.error('Error checking client existence:', checkError);
        throw new Error(`Could not verify client: ${checkError.message}`);
      }

      if (!existingClient) {
        throw new Error('Client not found');
      }

      if (existingClient.organization_id !== organizationId) {
        throw new Error('You do not have permission to update this client');
      }

      // Nessun campo scalare NON significa "niente da fare": le sole relazioni
      // (interessi/settori/tag/referenti) si sincronizzano più sotto. Il vecchio
      // return anticipato qui faceva perdere in silenzio gli update di sole
      // relazioni (es. chip inline del pannello dettagli).
      if (Object.keys(updateData).length > 0) {
        // Try update without select first
        const { error: updateError } = await supabase
          .from('clients')
          .update(updateData)
          .eq('id', id)
          .eq('organization_id', organizationId);

        if (updateError) {
          console.error('❌ [CLIENT STORE] Update error (without select):', updateError);
          console.error('❌ [CLIENT STORE] Update failed with details:', {
            code: updateError.code,
            message: updateError.message,
            details: updateError.details,
            hint: updateError.hint
          });
          throw new Error(`Failed to update client: ${updateError.message}`);
        }
      }

      // If update succeeds, fetch the updated data separately
      const { data, error } = await supabase
        .from('clients')
        .select(`*`)
        .eq('id', id)
        .eq('organization_id', organizationId)
        .maybeSingle();
      

      if (error) {
        console.error('Error updating client:', error);
        throw new Error(`Failed to update client: ${error.message}`);
      }

      if (!data) {
        throw new Error('Update failed: No rows were updated. The client may not exist or you may not have permission to update it.');
      }

      // =====================================================
      // CRITICAL PATH COMPLETE - Update Zustand store now
      // =====================================================

      // Fetch client states to determine lead/client categorization
      let clientStates: Array<{id: string; name: string; color: string; is_lead: boolean}> = [];
      try {
        const { data: statesData, error: statesError } = await supabase
          .from('client_states')
          .select('id, name, color, is_lead')
          .eq('organization_id', organizationId);

        if (!statesError) {
          clientStates = statesData || [];
        }
      } catch (error) {
        // Could not fetch client states, continue without classification
      }

      const oldClientState = clientStates?.find(state => state.id === oldStatusId);
      const newClientState = clientStates?.find(state => state.id === newStatusId);

      const wasLead = oldClientState?.is_lead === true;
      const isNowLead = newClientState?.is_lead === true;

      // Handle status changes - move between lists as needed
      if (wasLead !== isNowLead && newStatusId !== undefined) {
        if (wasLead && !isNowLead) {
          // Moving from lead to client
          const leads = get().leads.filter(l => l.id !== id);
          const clients = [...get().clients, data];
          set({ leads, clients });
        } else if (!wasLead && isNowLead) {
          // Moving from client to lead
          const clients = get().clients.filter(c => c.id !== id);
          const leads = [...get().leads, data];
          set({ clients, leads });
        }
        // Update selectedClient on category change
        const currentSelectedClient = get().selectedClient;
        if (currentSelectedClient?.id === id) {
          set({ selectedClient: { ...currentSelectedClient, ...data } });
        }
      } else {
        // No category change, update appropriate list
        if (isNowLead) {
          const leads = get().leads.map(l => l.id === id ? { ...l, ...data } : l);
          set({ leads });
        } else {
          const clients = get().clients.map(c => c.id === id ? { ...c, ...data } : c);
          set({ clients });
        }
      }

      // Also update selectedClient if it's the same record
      const currentSelectedClient = get().selectedClient;
      if (currentSelectedClient?.id === id) {
        set({ selectedClient: { ...currentSelectedClient, ...data } });
      }

      // =====================================================
      // SALVATAGGIO INTERESSI - SINCRONO (deve completare prima di ritornare)
      // =====================================================
      if (client.selected_interest_categories !== undefined) {
        console.log('[CLIENT STORE] Salvataggio interessi SINCRONO:', {
          clientId: id,
          organizationId,
          interestCategories: client.selected_interest_categories
        });

        try {
          // Fetch existing interests to compute diff (preserves created_at)
          const { data: existingInterests } = await supabase
            .from('client_interests')
            .select('interest_category_id')
            .eq('client_id', id)
            .eq('organization_id', organizationId);

          const existingIds = (existingInterests || []).map(i => i.interest_category_id).filter(Boolean) as string[];
          const newIds = client.selected_interest_categories;
          const toAdd = newIds.filter(catId => !existingIds.includes(catId));
          const toRemove = existingIds.filter(catId => !newIds.includes(catId));

          // Delete only removed interests
          if (toRemove.length > 0) {
            const { error: deleteError } = await supabase
              .from('client_interests')
              .delete()
              .eq('client_id', id)
              .eq('organization_id', organizationId)
              .in('interest_category_id', toRemove);

            if (deleteError) {
              console.error('[CLIENT STORE] Errore delete interessi:', deleteError);
            }
          }

          // Insert only new interests
          if (toAdd.length > 0) {
            const interestsToInsert = toAdd.map(categoryId => ({
              client_id: id,
              organization_id: organizationId,
              interest_category_id: categoryId
            }));

            const { error: insertError } = await supabase
              .from('client_interests')
              .insert(interestsToInsert);

            if (insertError) {
              console.error('[CLIENT STORE] Errore insert interessi:', insertError);
            } else {
              console.log('[CLIENT STORE] Interessi salvati con successo:', interestsToInsert.length);
            }
          } else if (toRemove.length === 0) {
            console.log('[CLIENT STORE] Nessuna modifica interessi');
          }

          // Aggiorna lo stato locale IMMEDIATAMENTE
          const interestCategories = client.selected_interest_categories;
          set(state => ({
            leads: state.leads.map(lead =>
              lead.id === id
                ? { ...lead, interest_categories: interestCategories }
                : lead
            ),
            clients: state.clients.map(c =>
              c.id === id
                ? { ...c, interest_categories: interestCategories }
                : c
            ),
            selectedClient: state.selectedClient?.id === id
              ? { ...state.selectedClient, interest_categories: interestCategories }
              : state.selectedClient
          }));
          console.log('[CLIENT STORE] Stato locale aggiornato con interessi');

          // Emit workflow trigger events for interest changes
          try {
            const { data: clientForEvent } = await supabase
              .from('clients')
              .select('stato_cliente, business_name, contact_name, email, phone')
              .eq('id', id)
              .single();
            if (clientForEvent) {
              const { data: stateInfo } = await supabase.from('client_states').select('is_lead').eq('id', clientForEvent.stato_cliente).single();
              const prefix = stateInfo?.is_lead ? 'lead' : 'client';
              for (const catId of toAdd) {
                eventBus.emitEvent({ type: `${prefix}:interest_added`, payload: {
                  clientId: id, interestCategoryId: catId, organizationId,
                  businessName: clientForEvent.business_name, contactName: clientForEvent.contact_name,
                  email: clientForEvent.email, phone: clientForEvent.phone,
                }} as any);
              }
              for (const catId of toRemove) {
                eventBus.emitEvent({ type: `${prefix}:interest_removed`, payload: {
                  clientId: id, interestCategoryId: catId, organizationId,
                  businessName: clientForEvent.business_name, contactName: clientForEvent.contact_name,
                  email: clientForEvent.email, phone: clientForEvent.phone,
                }} as any);
              }
            }
          } catch (e) { console.warn('[CLIENT STORE] Failed to emit interest workflow events:', e); }
        } catch (interestError) {
          console.error('[CLIENT STORE] Errore salvataggio interessi:', interestError);
        }
      }

      // =====================================================
      // SALVATAGGIO SETTORI - diff-based sync come interessi
      // =====================================================
      if (client.selected_settori !== undefined) {
        try {
          const { useSettoriStore } = await import('./settoriStore');
          await useSettoriStore.getState().assignSettoriToClient(id, client.selected_settori);

          // Aggiorna lo stato locale
          set(state => ({
            leads: state.leads.map(lead =>
              lead.id === id ? { ...lead, selected_settori: client.selected_settori } : lead
            ),
            clients: state.clients.map(c =>
              c.id === id ? { ...c, selected_settori: client.selected_settori } : c
            ),
            selectedClient: state.selectedClient?.id === id
              ? { ...state.selectedClient, selected_settori: client.selected_settori }
              : state.selectedClient
          }));
        } catch (settoriError) {
          console.error('[CLIENT STORE] Errore salvataggio settori:', settoriError);
        }
      }

      // =====================================================
      // SALVATAGGIO CONTATTI - SINCRONO con pattern UPSERT
      // Preserva gli ID esistenti per evitare CASCADE delete su deal_contacts
      // =====================================================
      if (client.contacts !== undefined) {
        // Referenti come persone reali + client_relationships (sostituisce client_contacts).
        // La modifica dei referenti avviene di norma dalla sezione "Persone" del detail;
        // qui gestiamo il salvataggio dal form (create/edit) tenendo la UI invariata.
        try {
          const { persistClientReferenti } = await import('../utils/persistClientContacts');
          // 1) Upsert dei referenti presenti (persone + link idempotenti)
          const keptPersonIds = new Set(await persistClientReferenti(id, organizationId, client.contacts));

          // 2) Disattiva i link 'contatto' verso persone non più presenti (diff per person id,
          //    stabile anche per i contatti senza email, a differenza del diff per email).
          const { data: currentLinks } = await supabase
            .from('client_relationships')
            .select('id, source_client_id')
            .eq('organization_id', organizationId)
            .eq('relationship_type', 'contatto')
            .eq('is_active', true)
            .eq('target_client_id', id);
          for (const link of currentLinks || []) {
            if (!keptPersonIds.has((link as any).source_client_id)) {
              await supabase
                .from('client_relationships')
                .update({ is_active: false })
                .eq('id', (link as any).id)
                .eq('organization_id', organizationId);
            }
          }

          // 3) Riflette i contatti passati nello stato locale
          set(state => ({
            leads: state.leads.map(lead => lead.id === id ? { ...lead, contacts: client.contacts } : lead),
            clients: state.clients.map(c => c.id === id ? { ...c, contacts: client.contacts } : c),
            selectedClient: state.selectedClient?.id === id
              ? { ...state.selectedClient, contacts: client.contacts }
              : state.selectedClient,
          }));
        } catch (contactError) {
          console.error('[CLIENT STORE] Errore salvataggio referenti:', contactError);
        }
      }

      // =====================================================
      // SALVATAGGIO SEDI OPERATIVE - SINCRONO con pattern UPSERT
      // Stesso pattern dei contatti per consistenza
      // =====================================================
      if (client.operational_locations !== undefined) {
        console.log('[CLIENT STORE] Salvataggio sedi operative UPSERT:', {
          clientId: id,
          organizationId,
          locationsCount: client.operational_locations.length
        });

        try {
          // 1. Recupera le sedi esistenti con i loro ID
          const { data: existingLocations } = await supabase
            .from('client_operational_locations')
            .select('id')
            .eq('client_id', id)
            .eq('organization_id', organizationId);

          const existingLocIds = new Set(existingLocations?.map(l => l.id) || []);

          // 2. Separa sedi: esistenti (hanno ID reale) vs nuove (hanno ID temp o niente)
          const isRealLocId = (locId: string | undefined) =>
            locId && !locId.startsWith('temp_') && existingLocIds.has(locId);

          const locationsToUpdate = client.operational_locations.filter(l => isRealLocId(l.id));
          const locationsToInsert = client.operational_locations.filter(l => !isRealLocId(l.id));

          // 3. Trova ID da eliminare (presenti in DB ma non più nella lista)
          const locIdsToKeep = new Set(locationsToUpdate.map(l => l.id));
          const locIdsToDelete = [...existingLocIds].filter(existingId => !locIdsToKeep.has(existingId));

          // 4. Elimina SOLO le sedi rimosse (non tutte!)
          if (locIdsToDelete.length > 0) {
            console.log('[CLIENT STORE] Eliminando sedi rimosse:', locIdsToDelete);
            const { error: deleteError } = await supabase
              .from('client_operational_locations')
              .delete()
              .in('id', locIdsToDelete);

            if (deleteError) {
              console.error('[CLIENT STORE] Errore delete sedi:', deleteError);
            }
          }

          // 5. Aggiorna sedi esistenti (preserva ID!)
          for (const location of locationsToUpdate) {
            const { error: updateError } = await supabase
              .from('client_operational_locations')
              .update({
                name: location.name,
                country: location.country || null,
                address: location.address || null,
                city: location.city || null,
                province: location.province || null,
                postal_code: location.postal_code || null,
                region: location.region || null,
                notes: location.notes || null,
                is_default: !!location.is_default,
                recipient_name: location.recipient_name || null,
                phone: location.phone || null,
                location_type: location.location_type || 'operational'
              })
              .eq('id', location.id);

            if (updateError) {
              console.error('[CLIENT STORE] Errore update sede:', location.id, updateError);
            }
          }

          // 6. Inserisci nuove sedi
          if (locationsToInsert.length > 0) {
            const newLocations = locationsToInsert.map(location => ({
              client_id: id,
              organization_id: organizationId,
              name: location.name,
              country: location.country || null,
              address: location.address || null,
              city: location.city || null,
              province: location.province || null,
              postal_code: location.postal_code || null,
              region: location.region || null,
              is_active: true,
              notes: location.notes || null,
              is_default: !!location.is_default,
              recipient_name: location.recipient_name || null,
              phone: location.phone || null,
              location_type: location.location_type || 'operational'
            }));

            console.log('[CLIENT STORE] Inserendo nuove sedi:', newLocations.length);
            const { error: insertError } = await supabase
              .from('client_operational_locations')
              .insert(newLocations);

            if (insertError) {
              console.error('[CLIENT STORE] Errore insert sedi:', insertError);
            }
          }

          console.log('[CLIENT STORE] Sedi salvate con successo (upsert pattern):', {
            updated: locationsToUpdate.length,
            inserted: locationsToInsert.length,
            deleted: locIdsToDelete.length
          });

          // Aggiorna lo stato locale IMMEDIATAMENTE
          set(state => ({
            leads: state.leads.map(lead =>
              lead.id === id
                ? { ...lead, operational_locations: client.operational_locations }
                : lead
            ),
            clients: state.clients.map(c =>
              c.id === id
                ? { ...c, operational_locations: client.operational_locations }
                : c
            ),
            selectedClient: state.selectedClient?.id === id
              ? { ...state.selectedClient, operational_locations: client.operational_locations }
              : state.selectedClient
          }));
          console.log('[CLIENT STORE] Stato locale aggiornato con sedi operative');
        } catch (locationError) {
          console.error('[CLIENT STORE] Errore salvataggio sedi operative:', locationError);
        }
      }

      // =====================================================
      // BACKGROUND OPERATIONS - Fire and forget
      // These run async without blocking the user
      // =====================================================

      // Capture all needed variables for background closure
      const bgOrganizationId = organizationId;
      const bgOldStatusId = oldStatusId;
      const bgNewStatusId = newStatusId;
      const bgOldPipelineId = oldPipelineId;
      const bgNewPipelineId = newPipelineId;
      const bgClientStates = clientStates;

      // Launch background operations without await
      (async () => {
        try {
          // Log state change if stato_cliente OR pipeline_stage_id OR lead_stage was updated
          // IMPORTANT: Only trigger if these fields were explicitly provided in the update
          // This prevents automations from triggering when only other fields (like assigned_to) are updated
          const statusFieldChanged = ('stato_cliente' in client && bgNewStatusId !== undefined && bgOldStatusId !== bgNewStatusId);
          const customPipelineStageChanged = ('pipeline_stage_id' in client && client.pipeline_stage_id !== undefined && currentClient.pipeline_stage_id !== client.pipeline_stage_id);
          const defaultPipelineStageChanged = ('lead_stage' in client && client.lead_stage !== undefined && currentClient.lead_stage !== client.lead_stage);
          const pipelineStageChanged = customPipelineStageChanged || defaultPipelineStageChanged;

          if (statusFieldChanged || pipelineStageChanged) {
            // Use the appropriate state ID based on which field changed
            const effectiveOldStateId = pipelineStageChanged
              ? (currentClient.pipeline_stage_id || currentClient.lead_stage)
              : bgOldStatusId;
            const effectiveNewStateId = pipelineStageChanged
              ? (client.pipeline_stage_id || client.lead_stage)
              : bgNewStatusId;
            try {
              // Verifica che il nuovo stato sia un UUID valido
              const isValidUUID = effectiveNewStateId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(effectiveNewStateId);
              const isOldValidUUID = effectiveOldStateId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(effectiveOldStateId);

              if (isValidUUID) {
                // Determine which table to check based on which field changed
                // Custom pipeline (pipeline_stage_id) → lead_pipeline_stages
                // Default pipeline (lead_stage) → lead_states
                // Client state (stato_cliente) → client_states
                const stateTable = customPipelineStageChanged
                  ? 'lead_pipeline_stages'
                  : defaultPipelineStageChanged
                    ? 'lead_states'
                    : 'client_states';

                // Validate that states exist in the appropriate table before logging
                let validatedOldStateId: string | undefined = undefined;

                // Check if old state exists (if it's a valid UUID)
                if (isOldValidUUID) {
                  const { data: oldStateExists } = await supabase
                    .from(stateTable)
                    .select('id')
                    .eq('id', effectiveOldStateId)
                    .eq('organization_id', bgOrganizationId)
                    .maybeSingle();

                  // Only use oldStateId if it actually exists in the table
                  if (oldStateExists) {
                    validatedOldStateId = effectiveOldStateId;
                  }
                }

                // Check if new state exists
                const { data: newStateExists } = await supabase
                  .from(stateTable)
                  .select('id')
                  .eq('id', effectiveNewStateId)
                  .eq('organization_id', bgOrganizationId)
                  .maybeSingle();

                if (!newStateExists) {
                  console.error(`[CLIENT STORE BG] New state ${effectiveNewStateId} does not exist in ${stateTable}, cannot log state change`);
                  // Don't throw in background - just log and continue
                } else {
                  // Only log to client_state_changes if using client_states table
                  // (lead_states and lead_pipeline_stages have separate foreign keys)
                  if (stateTable === 'client_states') {
                    const stateChangesStore = useClientStateChangesStore.getState();
                    await stateChangesStore.logStateChange({
                      clientId: id,
                      fromStateId: validatedOldStateId,
                      toStateId: effectiveNewStateId,
                      reason: 'Client status updated via form',
                      source: 'manual'
                    });
                  } else {
                    console.log(`[CLIENT STORE BG] Skipping client_state_changes log for ${stateTable} (not a client state)`);
                  }

                  // Trigger workflow automation system for lead_state_changed
                  try {
                    // Determine if this is a lead or client based on which field changed
                    let isLead = false;

                    if (customPipelineStageChanged) {
                      isLead = true;
                    } else if (defaultPipelineStageChanged) {
                      isLead = true;
                    } else {
                      // For client states (stato_cliente), check the is_lead flag in client_states
                      const { data: newState } = await supabase
                        .from('client_states')
                        .select('is_lead')
                        .eq('id', effectiveNewStateId)
                        .eq('organization_id', bgOrganizationId)
                        .single();
                      isLead = newState?.is_lead || false;
                    }

                    if (isLead) {
                      // Get the lead data to access pipeline_id and business_name
                      const { data: leadData } = await supabase
                        .from('clients')
                        .select('pipeline_id, business_name')
                        .eq('id', id)
                        .single();

                      console.log('🔔 [CLIENT STORE BG] Emitting lead:state_changed event', {
                        from_state_id: validatedOldStateId,
                        to_state_id: effectiveNewStateId,
                        pipeline_id: leadData?.pipeline_id
                      });

                      eventBus.emitEvent({
                        type: 'lead:state_changed',
                        payload: {
                          leadId: id,
                          pipelineId: leadData?.pipeline_id,
                          fromStateId: validatedOldStateId,
                          toStateId: effectiveNewStateId,
                          businessName: leadData?.business_name
                        }
                      });
                    } else {
                      // This is a client (not a lead) - emit client:state_changed event
                      const { data: clientData } = await supabase
                        .from('clients')
                        .select('business_name')
                        .eq('id', id)
                        .single();

                      console.log('🔔 [CLIENT STORE BG] Emitting client:state_changed event', {
                        from_state_id: validatedOldStateId,
                        to_state_id: effectiveNewStateId
                      });

                      eventBus.emitEvent({
                        type: 'client:state_changed',
                        payload: {
                          clientId: id,
                          fromStateId: validatedOldStateId,
                          toStateId: effectiveNewStateId,
                          businessName: clientData?.business_name
                        }
                      });
                    }
                  } catch (eventError) {
                    console.error('[CLIENT STORE BG] Failed to emit event:', eventError);
                  }
                }
              }
            } catch (logError) {
              console.error('[CLIENT STORE BG] Failed to log state change:', logError);
            }
          }

          // Emit lead:pipeline_changed event if pipeline was explicitly changed
          if ('pipeline_id' in client && bgNewPipelineId !== undefined && bgOldPipelineId !== bgNewPipelineId) {
            try {
              // Check if this is a lead
              const { data: stateData } = await supabase
                .from('client_states')
                .select('is_lead')
                .eq('id', data.stato_cliente)
                .maybeSingle();

              if (stateData?.is_lead) {
                console.log('🔔 [CLIENT STORE BG] Emitting lead:pipeline_changed event', {
                  leadId: id,
                  fromPipelineId: bgOldPipelineId,
                  toPipelineId: bgNewPipelineId
                });

                eventBus.emitEvent({
                  type: 'lead:pipeline_changed',
                  payload: {
                    leadId: id,
                    fromPipelineId: bgOldPipelineId,
                    toPipelineId: bgNewPipelineId,
                    businessName: data.business_name
                  }
                });
              }
            } catch (eventError) {
              console.error('[CLIENT STORE BG] Failed to emit lead:pipeline_changed event:', eventError);
            }
          }

          // Emit lead:assigned quando cambia il responsabile (riassegnazione o prima assegnazione).
          // Serve al NotificationTriggerService per avvisare il nuovo assegnatario:
          // senza questo evento la riassegnazione resta muta (bug storico, fix 2 del 16/08).
          if ('assigned_to' in client && client.assigned_to !== undefined
              && updateData.assigned_to
              && updateData.assigned_to !== currentClient.assigned_to) {
            try {
              const { data: stateData } = await supabase
                .from('client_states')
                .select('is_lead')
                .eq('id', data.stato_cliente)
                .maybeSingle();

              console.log('🔔 [CLIENT STORE BG] Emitting lead:assigned event', {
                leadId: id,
                assignedTo: updateData.assigned_to,
                previousAssignedTo: currentClient.assigned_to
              });

              eventBus.emitEvent({
                type: 'lead:assigned',
                payload: {
                  leadId: id,
                  assignedTo: updateData.assigned_to,
                  previousAssignedTo: currentClient.assigned_to,
                  businessName: data.business_name,
                  isLead: stateData?.is_lead === true
                }
              });
            } catch (eventError) {
              console.error('[CLIENT STORE BG] Failed to emit lead:assigned event:', eventError);
            }
          }

          // Emit lead:updated event for field changes (excluding state/pipeline changes)
          // Only emit if this is a lead and there are actual field changes
          if (Object.keys(updateData).length > 0) {
            try {
              // Check if this is a lead
              const { data: stateData } = await supabase
                .from('client_states')
                .select('is_lead')
                .eq('id', data.stato_cliente)
                .maybeSingle();

              if (stateData?.is_lead) {
                // Get the list of fields that changed (excluding internal fields like updated_at)
                const fieldsChanged = Object.keys(updateData).filter(
                  field => !['updated_at'].includes(field)
                );

                // Only emit if there are meaningful field changes
                if (fieldsChanged.length > 0) {
                  console.log('🔔 [CLIENT STORE BG] Emitting lead:updated event', {
                    leadId: id,
                    fieldsChanged
                  });

                  eventBus.emitEvent({
                    type: 'lead:updated',
                    payload: {
                      leadId: id,
                      pipelineId: data.pipeline_id,
                      fieldsChanged,
                      businessName: data.business_name
                    }
                  });
                }
              } else {
                // This is a client (not a lead)
                const fieldsChanged = Object.keys(updateData).filter(
                  field => !['updated_at'].includes(field)
                );

                if (fieldsChanged.length > 0) {
                  console.log('🔔 [CLIENT STORE BG] Emitting client:updated event', {
                    clientId: id,
                    fieldsChanged
                  });

                  eventBus.emitEvent({
                    type: 'client:updated',
                    payload: {
                      clientId: id,
                      fieldsChanged,
                      businessName: data.business_name
                    }
                  });
                }
              }
            } catch (eventError) {
              console.error('[CLIENT STORE BG] Failed to emit updated event:', eventError);
            }
          }

          // Sync client to newsletter lists when relevant fields change
          // This creates records in newsletter_subscribers for matching lists
          try {
            // Determine if this is a lead
            const { data: stateInfo } = await supabase
              .from('client_states')
              .select('is_lead')
              .eq('id', data.stato_cliente)
              .maybeSingle();

            const isLead = stateInfo?.is_lead ?? false;
            const orgId = data.organization_id || organizationId;

            if (orgId) {
              newsletterListSyncService.syncClientToListsAsync(id, orgId, isLead);
            }
          } catch (syncError) {
            console.error('[CLIENT STORE BG] Newsletter sync error (non-blocking):', syncError);
          }

          // Handle tag assignments if provided
          if (client.selected_tags !== undefined) {
            try {
              const tagStore = useClientTagStore.getState();
              await tagStore.assignTagsToClient(id, client.selected_tags);

              // If this is the currently selected client, refresh it to show updated tags
              const currentSelectedClient = get().selectedClient;
              if (currentSelectedClient && currentSelectedClient.id === id) {
                await get().fetchClientById(id, true); // Force refresh
              }
            } catch (tagError) {
              console.error('[CLIENT STORE BG] Failed to assign tags:', tagError);
            }
          }

          // NOTA: Il salvataggio degli interessi e dei contatti è stato spostato FUORI dal background
          // per garantire che completino PRIMA del return. Vedi sezioni "SALVATAGGIO INTERESSI - SINCRONO" e "SALVATAGGIO CONTATTI - SINCRONO"

        } catch (bgError) {
          console.error('[CLIENT STORE] Background operations error:', bgError);
          // Background errors don't affect the main update result
        }
      })();

      // Auto re-fetch logo if email or website changed and logo_url was not explicitly set
      if ((client.email !== undefined || client.website !== undefined) && client.logo_url === undefined) {
        try {
          const { getClientDomain, fetchBestLogoUrl } = await import('../utils/companyLogoService');
          const newEmail = client.email !== undefined ? client.email : currentClient.email;
          const newWebsite = client.website !== undefined ? client.website : (data as any).website;
          const domain = getClientDomain(newEmail, newWebsite);
          if (domain) {
            fetchBestLogoUrl(domain).then(async (logoUrl) => {
              if (logoUrl) {
                await supabase.from('clients').update({ logo_url: logoUrl }).eq('id', id);
                set(state => ({
                  clients: state.clients.map(c => c.id === id ? { ...c, logo_url: logoUrl } : c),
                  leads: state.leads.map(l => l.id === id ? { ...l, logo_url: logoUrl } : l),
                  selectedClient: state.selectedClient?.id === id
                    ? { ...state.selectedClient, logo_url: logoUrl }
                    : state.selectedClient
                }));
              }
            }).catch(() => { /* non-blocking */ });
          }
        } catch { /* non-blocking */ }
      }

      // Ri-geocodifica in background se l'indirizzo è cambiato
      const addressChanged = client.street_address !== undefined || client.city !== undefined
        || client.province !== undefined || client.country !== undefined;
      if (addressChanged) {
        geocodeAddress({
          street_address: client.street_address ?? data?.street_address,
          street_number: client.street_number ?? data?.street_number,
          city: client.city ?? data?.city,
          province: client.province ?? data?.province,
          postal_code: client.postal_code ?? data?.postal_code,
          country: client.country ?? data?.country,
        }).then(geo => {
          if (geo) {
            supabase.from('clients').update({
              latitude: geo.latitude,
              longitude: geo.longitude,
              geocoded_at: geo.geocoded_at,
            }).eq('id', id).then(() => {
              set(state => ({
                clients: state.clients.map(c => c.id === id ? { ...c, ...geo } : c),
                leads: state.leads.map(l => l.id === id ? { ...l, ...geo } : l),
                selectedClient: state.selectedClient?.id === id
                  ? { ...state.selectedClient, ...geo }
                  : state.selectedClient
              }));
            });
          }
        }).catch(() => {});
      }

      // Return immediately after store update - background operations continue async
      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  deleteClient: async (id: string) => {
    try {
      // Get current user's organization ID from cache
      const organizationId = await getCurrentUserOrganizationId();
      if (!organizationId) throw new Error('User not associated with an organization');

      // Get client info before deletion for logging
      const { data: clientToDelete } = await supabase
        .from('clients')
        .select('id, business_name, stato_cliente')
        .eq('id', id)
        .eq('organization_id', organizationId)
        .single();

      // Rimuovi i record da newsletter_subscribers (non hanno FK cascade)
      const { error: subscriberError } = await supabase
        .from('newsletter_subscribers')
        .delete()
        .eq('entity_id', id)
        .eq('organization_id', organizationId);

      if (subscriberError) {
        console.warn('[CLIENT STORE] Errore rimozione newsletter_subscribers:', subscriberError);
        // Non bloccare l'eliminazione del client
      }

      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', id)
        .eq('organization_id', organizationId);

      if (error) throw error;

      // Find the deleted lead to get its stage for pagination update
      const deletedLead = get().leads.find(l => l.id === id);
      const clients = get().clients.filter(c => c.id !== id);
      const leads = get().leads.filter(l => l.id !== id);

      // Update pagination totalCount for the stage
      const currentPagination = get().leadsPagination;
      let updatedPagination = currentPagination;

      if (deletedLead) {
        const stageId = deletedLead.pipeline_id
          ? deletedLead.pipeline_stage_id
          : deletedLead.lead_stage;

        if (stageId && currentPagination.byStage[stageId]) {
          updatedPagination = {
            ...currentPagination,
            byStage: {
              ...currentPagination.byStage,
              [stageId]: {
                ...currentPagination.byStage[stageId],
                totalCount: Math.max(0, (currentPagination.byStage[stageId].totalCount ?? 0) - 1),
                totalLoaded: Math.max(0, currentPagination.byStage[stageId].totalLoaded - 1)
              }
            }
          };
        }
      }

      set({ clients, leads, leadsPagination: updatedPagination });
      return true;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return false;
    }
  },

  convertLeadToClient: async (id: string) => {
    try {
      // Get current user's organization ID from cache
      const organizationId = await getCurrentUserOrganizationId();
      if (!organizationId) throw new Error('User not associated with an organization');

      // Get the first active client state (is_lead = false) ordered by sort_order
      const { data: firstClientState } = await supabase
        .from('client_states')
        .select('id')
        .eq('organization_id', organizationId)
        .eq('is_lead', false)
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .limit(1)
        .single();

      const targetStateId = firstClientState?.id || 'cliente'; // fallback if no states configured

      // Update the client's stato_cliente field to the first client state
      const { error: updateError } = await supabase
        .from('clients')
        .update({
          stato_cliente: targetStateId,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('organization_id', organizationId);

      if (updateError) throw updateError;

      // Fetch base client data (senza JOIN che fallisce - il FK non esiste)
      const { data, error: fetchError } = await supabase
        .from('clients')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      // Get client states from cache and enrich manually (stesso pattern di fetchClients)
      const clientStates = await getCachedClientStates(organizationId);
      const stateMap = new Map(clientStates?.map(state => [state.id, state]) || []);

      const enrichedClient = {
        ...data,
        client_state: data.stato_cliente ? stateMap.get(data.stato_cliente) : null,
        tags: [] // Tags will be fetched when navigating to /clients
      };

      // Emit event for workflow triggers
      try {
        console.log('🔄 [CLIENT STORE] Emitting lead:converted_to_client event');
        eventBus.emitEvent({
          type: 'lead:converted_to_client',
          payload: {
            leadId: id,
            clientId: enrichedClient.id,
            businessName: enrichedClient.business_name
          }
        });
      } catch (eventError) {
        console.error('Error emitting event:', eventError);
      }

      // Update the leads and clients arrays with enriched data
      const leads = get().leads.filter(l => l.id !== id);
      const clients = [...get().clients, enrichedClient];
      set({ leads, clients });

      return true;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return false;
    }
  },
  
  // Imposta l'assegnatario selezionato per il filtro
  setSelectedUser: (userId) => {
    set({ selectedUserId: userId });
    // Re-fetch clients and leads with new filter
    const salesRepId = get().selectedSalesRepId;
    get().fetchClients(userId, salesRepId);
    get().fetchLeads(userId, salesRepId);
  },

  // Imposta il sales rep selezionato per il filtro
  setSelectedSalesRep: (userId) => {
    set({ selectedSalesRepId: userId });
    // Re-fetch clients and leads with new filter
    const assignedToId = get().selectedUserId;
    get().fetchClients(assignedToId, userId);
    get().fetchLeads(assignedToId, userId);
  },
  
  // Restituisce i clienti filtrati per assegnatario e/o sales rep
  getFilteredClients: () => {
    const { clients, selectedUserId, selectedSalesRepId } = get();

    if (!selectedUserId && !selectedSalesRepId) {
      return clients;
    }

    return clients.filter(client => {
      const matchesAssignedTo = !selectedUserId || client.assigned_to === selectedUserId;
      const matchesSalesRep = !selectedSalesRepId || client.sales_rep_id === selectedSalesRepId;
      return matchesAssignedTo && matchesSalesRep;
    });
  },

  // Update a single lead in the leads array (for Realtime updates)
  updateSingleLead: (leadData: Client) => {
    set(state => {
      // Check if the lead has been converted to a customer by checking the client state
      // We need to check if the new stato_cliente is a customer state (is_lead = false)
      const customStatesStore = useCustomStatesStore.getState();
      const newClientState = customStatesStore.clientStates.find(s => s.id === leadData.stato_cliente);

      // If the new state is a customer state (is_lead = false), remove from leads array
      if (newClientState && !newClientState.is_lead) {
        console.log(`[ClientStore] Lead ${leadData.id} converted to customer (state: ${newClientState.name}), removing from leads array`);
        return {
          leads: state.leads.filter(lead => lead.id !== leadData.id)
        };
      }

      // Otherwise, update the lead normally
      return {
        leads: state.leads.map(lead => {
          if (lead.id === leadData.id) {
            // Preserve existing nested objects if not provided in update
            // This prevents breaking the UI when Realtime payload lacks joined data
            return {
              ...lead,
              ...leadData,
              // Preserve assigned_user if leadData doesn't include it or it's explicitly null
              assigned_user: leadData.assigned_user !== undefined
                ? leadData.assigned_user
                : lead.assigned_user,
              // Preserve pipeline if not provided
              pipeline: leadData.pipeline !== undefined
                ? leadData.pipeline
                : lead.pipeline,
              // Preserve pipeline_stage if not provided
              pipeline_stage: leadData.pipeline_stage !== undefined
                ? leadData.pipeline_stage
                : lead.pipeline_stage,
              // Preserve tags if not provided
              tags: leadData.tags !== undefined
                ? leadData.tags
                : lead.tags,
            };
          }
          return lead;
        })
      };
    });
  },

  // Add a new lead to the leads array (for Realtime INSERT events)
  addSingleLead: (leadData: Client) => {
    set(state => {
      // Verifica che sia effettivamente un lead (is_lead = true)
      const customStatesStore = useCustomStatesStore.getState();
      const clientState = customStatesStore.clientStates.find(s => s.id === leadData.stato_cliente);

      // Se non è un lead (is_lead = false), ignora
      if (clientState && !clientState.is_lead) {
        console.log(`[ClientStore] New client ${leadData.id} is not a lead (state: ${clientState.name}), skipping`);
        return state;
      }

      // Verifica che non esista già (evita duplicati)
      if (state.leads.some(lead => lead.id === leadData.id)) {
        console.log(`[ClientStore] Lead ${leadData.id} already exists, skipping`);
        return state;
      }

      console.log(`[ClientStore] Adding new lead ${leadData.id} via Realtime INSERT`);

      // Mantieni allineato il contatore per-stage del Kanban (mirror del decremento in deleteClient).
      const stageId = getEffectiveStageId(leadData);
      const stagePag = stageId ? state.leadsPagination.byStage[stageId] : undefined;
      const leadsPagination = (stageId && stagePag)
        ? {
            ...state.leadsPagination,
            byStage: {
              ...state.leadsPagination.byStage,
              [stageId]: {
                ...stagePag,
                totalCount: (stagePag.totalCount ?? 0) + 1,
                totalLoaded: (stagePag.totalLoaded ?? 0) + 1
              }
            }
          }
        : state.leadsPagination;

      // Aggiungi il nuovo lead all'inizio dell'array
      return {
        leads: [leadData, ...state.leads],
        leadsPagination
      };
    });
  },

  getFilteredLeads: () => {
    const { leads, selectedUserId, selectedSalesRepId } = get();

    if (!selectedUserId && !selectedSalesRepId) {
      return leads;
    }

    return leads.filter(lead => {
      const matchesAssignedTo = !selectedUserId || lead.assigned_to === selectedUserId;
      const matchesSalesRep = !selectedSalesRepId || lead.sales_rep_id === selectedSalesRepId;
      return matchesAssignedTo && matchesSalesRep;
    });
  },

  // =====================================================
  // SERVER-SIDE SEARCH FOR CLIENT/LEAD DROPDOWN
  // =====================================================

  searchClientsAndLeads: async (searchTerm: string, assignedToUserIds?: string[], limit = 50) => {
    if (!searchTerm || searchTerm.length < 2) return [];

    try {
      const organizationId = await getCurrentUserOrganizationId();
      if (!organizationId) return [];

      // Il termine va ripulito: virgole e parentesi sono sintassi dei filtri PostgREST.
      const safeTerm = sanitizeFilterValue(searchTerm);
      if (!safeTerm) return [];

      // Query senza join - gli stati vengono dalla cache
      let query = supabase
        .from('clients')
        .select(`
          id,
          business_name,
          trade_name,
          entity_type,
          email,
          phone,
          whatsapp_number,
          stato_cliente,
          assigned_to,
          vat_number,
          pipeline_id,
          pipeline_stage_id
        `)
        .eq('organization_id', organizationId)
        .or(buildClientSearchOr(safeTerm, searchTerm))
        .or(EXCLUDE_REFERENTI); // Fase 2: no persone-referente negli autocomplete cliente/lead

      // Applica filtro utente se specificato (per permessi "vede solo i propri")
      if (assignedToUserIds && assignedToUserIds.length > 0) {
        query = query.in('assigned_to', assignedToUserIds);
      }

      const { data, error } = await query
        .order('business_name')
        .limit(limit);

      if (error) {
        console.error('Error searching clients/leads:', error);
        return [];
      }

      // Fetch stati dalla cache e arricchisci i risultati
      const clientStates = await getCachedClientStates(organizationId);
      const stateMap = new Map(clientStates?.map(state => [state.id, state]) || []);

      const enrichedResults = (data || []).map(client => ({
        ...client,
        client_state: client.stato_cliente ? stateMap.get(client.stato_cliente) : null
      }));

      return enrichedResults as Client[];
    } catch (error) {
      console.error('Error in searchClientsAndLeads:', error);
      return [];
    }
  },

  // =====================================================
  // PAGINATION METHODS FOR LEADS KANBAN
  // =====================================================

  setLeadsFilters: (filters: LeadsFilters) => {
    set({ currentLeadsFilters: filters });
  },

  resetLeadsPagination: () => {
    // Reset both pagination state AND leads array
    // This is called when pipeline/filters change and we need a fresh start
    set({
      leadsPagination: defaultPaginationState,
      leads: []
    });
  },

  fetchLeadsByStage: async (stageId, pipelineId, options = {}) => {
    const { cursor, limit = 50, filters, reset = false } = options;
    const { leadsPagination, currentLeadsFilters } = get();
    // NOTE: Don't read 'leads' here - read it after await to avoid race condition
    const isLegacyPipeline = !pipelineId || pipelineId === 'legacy';
    const activeFilters = filters || currentLeadsFilters;
    // Restrizioni di visibilità del ruolo (pipeline / fonti), opzionali
    const leadVisibility = usePermissionsStore.getState().getLeadVisibility();

    // Get current stage pagination state
    const stagePagination = leadsPagination.byStage[stageId] || defaultStagePaginationState;

    // If already loading this stage, skip
    if (stagePagination.loading && !reset) {
      return;
    }

    // Set loading state for this stage
    set({
      leadsPagination: {
        ...leadsPagination,
        byStage: {
          ...leadsPagination.byStage,
          [stageId]: { ...stagePagination, loading: true }
        }
      }
    });

    try {
      const organizationId = await getCurrentUserOrganizationId();
      if (!organizationId) throw new Error('User not associated with an organization');

      // Fetch client states — filter based on tipologiaFilter
      const clientStates = await getCachedClientStates(organizationId);
      const tipologia = activeFilters.tipologiaFilter || 'tutti';
      let stateFilterIds: string[] = [];
      if (tipologia === 'lead') {
        stateFilterIds = clientStates?.filter(state => state.is_lead).map(state => state.id) || [];
      } else if (tipologia === 'cliente') {
        stateFilterIds = clientStates?.filter(state => !state.is_lead).map(state => state.id) || [];
      }
      // tipologia === 'tutti' → stateFilterIds resta vuoto → nessun filtro stato
      // Filtro "Stato" della barra kanban (richiesta Lovati & Associati, 02/09/2026):
      // si interseca con la tipologia; se non resta nulla si forza un id impossibile,
      // altrimenti una lista vuota significherebbe "nessun filtro".
      const NO_MATCH_STATE = '00000000-0000-0000-0000-000000000000';
      const selectedStateIds = activeFilters.clientStateIds || [];
      let leadStateIds = stateFilterIds;
      if (selectedStateIds.length > 0) {
        leadStateIds = stateFilterIds.length > 0
          ? stateFilterIds.filter(id => selectedStateIds.includes(id))
          : selectedStateIds;
        if (leadStateIds.length === 0) leadStateIds = [NO_MATCH_STATE];
      }

      // Determine sort order (default: desc = più recenti prima)
      const sortAscending = activeFilters.sortOrder === 'asc';
      const effectiveCursor = reset ? null : (cursor || stagePagination.cursor);

      // ========================================
      // USE RPC when interest or tag filters are active
      // This avoids URL length limits with many UUIDs
      // ========================================
      const useRpc = (activeFilters.interestCategoryIds && activeFilters.interestCategoryIds.length > 0) ||
                     (activeFilters.tagIds && activeFilters.tagIds.length > 0) ||
                     (activeFilters.settoreIds && activeFilters.settoreIds.length > 0);

      if (useRpc) {
        // Prepare user filter params (filter out __unassigned__ - not a valid UUID for RPC)
        const UNASSIGNED = '__unassigned__';
        const INACTIVE_OWNER = '__inactive_owner__';
        const assignedToIds: string[] = [];
        const salesRepIds: string[] = [];
        activeFilters.userFilters?.forEach(uf => {
          // Non sono id: la RPC non li sa esprimere, la pagina raffina lato client.
          if (uf.userId === UNASSIGNED || uf.userId === INACTIVE_OWNER) return;
          if (uf.mode === 'assigned_to') assignedToIds.push(uf.userId);
          else if (uf.mode === 'sales_rep_id') salesRepIds.push(uf.userId);
          else { assignedToIds.push(uf.userId); salesRepIds.push(uf.userId); }
        });

        // Call RPC for filtered data
        const { data: rpcData, error: rpcError } = await supabase.rpc('get_leads_filtered', {
          p_organization_id: organizationId,
          p_pipeline_id: isLegacyPipeline ? null : pipelineId,
          p_stage_id: stageId,
          p_is_legacy_pipeline: isLegacyPipeline,
          p_stato_cliente_ids: leadStateIds.length > 0 ? leadStateIds : null,
          p_assigned_to_ids: assignedToIds.length > 0 ? assignedToIds : null,
          p_sales_rep_ids: salesRepIds.length > 0 ? salesRepIds : null,
          p_entity_types: activeFilters.entityTypes?.length ? activeFilters.entityTypes : null,
          p_countries: activeFilters.countries?.length ? activeFilters.countries : null,
          p_lead_sources: activeFilters.sources?.length ? activeFilters.sources : null,
          p_tag_ids: activeFilters.tagIds?.length ? activeFilters.tagIds : null,
          p_interest_category_ids: activeFilters.interestCategoryIds?.length ? activeFilters.interestCategoryIds : null,
          p_settore_ids: activeFilters.settoreIds?.length ? activeFilters.settoreIds : null,
          p_cursor: effectiveCursor || null,
          p_limit: limit,
          p_sort_ascending: sortAscending
        });

        if (rpcError) throw rpcError;

        // Get count (only on reset)
        let totalCount: number | undefined;
        if (reset) {
          const { data: countData } = await supabase.rpc('get_leads_filtered_count', {
            p_organization_id: organizationId,
            p_pipeline_id: isLegacyPipeline ? null : pipelineId,
            p_stage_id: stageId,
            p_is_legacy_pipeline: isLegacyPipeline,
            p_stato_cliente_ids: leadStateIds.length > 0 ? leadStateIds : null,
            p_assigned_to_ids: assignedToIds.length > 0 ? assignedToIds : null,
            p_sales_rep_ids: salesRepIds.length > 0 ? salesRepIds : null,
            p_entity_types: activeFilters.entityTypes?.length ? activeFilters.entityTypes : null,
            p_countries: activeFilters.countries?.length ? activeFilters.countries : null,
            p_lead_sources: activeFilters.sources?.length ? activeFilters.sources : null,
            p_tag_ids: activeFilters.tagIds?.length ? activeFilters.tagIds : null,
            p_interest_category_ids: activeFilters.interestCategoryIds?.length ? activeFilters.interestCategoryIds : null,
            p_settore_ids: activeFilters.settoreIds?.length ? activeFilters.settoreIds : null
          });
          totalCount = typeof countData === 'number' ? countData : undefined;
        }

        // Fetch related data for RPC results (tags, interests, etc.)
        const rpcLeadIds = rpcData?.map((c: any) => c.id) || [];

        // Helper riusato anche per i lead extra-pipeline (junction) più sotto,
        // che per costruzione NON sono in rpcLeadIds
        const fetchSettoriAssignments = async (clientIds: string[]): Promise<any[]> => {
          if (clientIds.length === 0) return [];
          const { data, error } = await supabase
            .from('client_settori')
            .select(`
              client_id,
              id,
              settore_id,
              created_at,
              settore:settori (
                id,
                name,
                color
              )
            `)
            .eq('organization_id', organizationId)
            .in('client_id', clientIds);
          if (error) {
            console.warn('[clientStore] Error fetching client_settori (RPC path):', error);
            return [];
          }
          return data || [];
        };

        // Le 4 fetch di arricchimento sono indipendenti: in parallelo
        const [tagAssignments, interestAssignments, settoriAssignments, metadataRows] = await Promise.all([
          // Tags
          (async (): Promise<any[]> => {
            if (rpcLeadIds.length === 0) return [];
            const { data, error } = await supabase
              .from('client_tag_assignments')
              .select(`
                client_id,
                assigned_at,
                client_tags!inner (
                  id,
                  name,
                  description,
                  color,
                  sort_order
                )
              `)
              .eq('organization_id', organizationId)
              .eq('client_tags.is_active', true)
              .in('client_id', rpcLeadIds);
            if (error) {
              console.warn('[clientStore] Error fetching client_tag_assignments (RPC path):', error);
              return [];
            }
            return data || [];
          })(),
          // Interests
          (async (): Promise<any[]> => {
            if (rpcLeadIds.length === 0) return [];
            const { data, error } = await supabase
              .from('client_interests')
              .select(`
                client_id,
                interest_category_id,
                created_at,
                interest_category:interest_categories (
                  id,
                  name,
                  color
                )
              `)
              .eq('organization_id', organizationId)
              .in('client_id', rpcLeadIds);
            if (error) {
              console.warn('[clientStore] Error fetching client_interests (RPC path):', error);
              return [];
            }
            return data || [];
          })(),
          // Settori
          fetchSettoriAssignments(rpcLeadIds),
          // Metadata (get_leads_filtered non ritorna la colonna metadata; serve al
          // raffinamento client-side del filtro "Studio AlfaDocs" in LeadsPage.tsx)
          (async (): Promise<any[]> => {
            if (rpcLeadIds.length === 0) return [];
            const { data, error } = await supabase
              .from('clients')
              .select('id, metadata, ad_platform')
              .in('id', rpcLeadIds);
            if (error) {
              console.warn('[clientStore] Error fetching clients metadata (RPC path):', error);
              return [];
            }
            return data || [];
          })()
        ]);

        // Create maps
        const stateMap = new Map(clientStates?.map(state => [state.id, state]) || []);
        const metadataMap = new Map<string, any>();
        const adPlatformMap = new Map<string, string | null>();
        metadataRows.forEach(row => {
          metadataMap.set(row.id, row.metadata);
          adPlatformMap.set(row.id, row.ad_platform ?? null);
        });
        const tagsMap = new Map<string, any[]>();
        tagAssignments.forEach(assignment => {
          if (!tagsMap.has(assignment.client_id)) {
            tagsMap.set(assignment.client_id, []);
          }
          tagsMap.get(assignment.client_id)!.push({ ...assignment.client_tags, assigned_at: assignment.assigned_at });
        });
        tagsMap.forEach((tags) => {
          tags.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        });

        const interestsMap = new Map<string, any[]>();
        interestAssignments.forEach(assignment => {
          if (!interestsMap.has(assignment.client_id)) {
            interestsMap.set(assignment.client_id, []);
          }
          interestsMap.get(assignment.client_id)!.push(assignment);
        });

        const settoriByClientMap = new Map<string, any[]>();
        settoriAssignments.forEach(assignment => {
          if (!settoriByClientMap.has(assignment.client_id)) {
            settoriByClientMap.set(assignment.client_id, []);
          }
          settoriByClientMap.get(assignment.client_id)!.push(assignment);
        });

        // Fetch user profiles for assigned_to and sales_rep_id
        const userIdSet = new Set<string>();
        rpcData?.forEach((c: any) => {
          if (c.assigned_to) userIdSet.add(c.assigned_to);
          if (c.sales_rep_id) userIdSet.add(c.sales_rep_id);
        });
        const userIds = Array.from(userIdSet);
        const userMap = new Map<string, any>();
        if (userIds.length > 0) {
          const { data: usersData } = await supabase
            .from('user_profiles')
            .select('id, email, full_name, phone, avatar_url, profile_image_url')
            .in('id', userIds);
          usersData?.forEach((u: any) => userMap.set(u.id, u));
        }

        // Enrich leads from RPC
        // La RPC non restituisce ad_platform: la restrizione fonte/attribuzione del
        // ruolo si applica qui con il valore letto insieme ai metadata.
        const enrichedLeads: any[] = (rpcData || [])
          .filter((c: any) => isLeadVisible({ ...c, ad_platform: adPlatformMap.get(c.id) ?? null }, leadVisibility))
          .map((client: any) => ({
          ...client,
          metadata: client.metadata ?? metadataMap.get(client.id) ?? null,
          client_state: client.stato_cliente ? stateMap.get(client.stato_cliente) : null,
          assigned_user: client.assigned_to ? userMap.get(client.assigned_to) || null : null,
          sales_rep: client.sales_rep_id ? userMap.get(client.sales_rep_id) || null : null,
          tags: tagsMap.get(client.id) || [],
          interests: interestsMap.get(client.id) || [],
          interest_categories: (interestsMap.get(client.id) || []).map((i: any) => i.interest_category_id).filter(Boolean),
          settori: settoriByClientMap.get(client.id) || []
        })) || [];

        // MULTI-PIPELINE: fetch extra leads from junction table (RPC path)
        if (!isLegacyPipeline && pipelineId && (reset || !effectiveCursor)) {
          try {
            const primaryIds = new Set(enrichedLeads.map((l: any) => l.id));
            const extraMemberships = (await fetchPipelineMembershipsCached(pipelineId))
              .filter(m => m.pipeline_stage_id === stageId);

            const extraClientIds = (extraMemberships || [])
              .filter(m => !primaryIds.has(m.client_id))
              .map(m => m.client_id);

            if (extraClientIds.length > 0) {
              let extraQuery = supabase
                .from('clients')
                .select('*')
                .in('id', extraClientIds);
              if (stateFilterIds.length > 0) {
                extraQuery = extraQuery.in('stato_cliente', stateFilterIds);
              }
              const { data: extraClientsData } = await extraQuery;

              // Settori anche per i lead extra: esclusi da rpcLeadIds per costruzione,
              // senza questo fetch avrebbero settori=[] e il raffinamento client-side
              // del filtro Settore in LeadsPage li nasconderebbe tutti
              const extraSettori = await fetchSettoriAssignments(
                (extraClientsData || []).map((c: any) => c.id)
              );
              extraSettori.forEach(assignment => {
                if (!settoriByClientMap.has(assignment.client_id)) {
                  settoriByClientMap.set(assignment.client_id, []);
                }
                settoriByClientMap.get(assignment.client_id)!.push(assignment);
              });

              const membershipMap = new Map((extraMemberships || []).map(m => [m.client_id, m]));
              const enrichedExtras = (extraClientsData || []).map((client: any) => ({
                ...client,
                client_state: client.stato_cliente ? stateMap.get(client.stato_cliente) : null,
                tags: tagsMap.get(client.id) || [],
                interests: interestsMap.get(client.id) || [],
                interest_categories: [],
                settori: settoriByClientMap.get(client.id) || [],
                _isExtraPipeline: true,
                _membershipId: membershipMap.get(client.id)?.id,
                _membershipStageId: stageId
              }));
              enrichedLeads.push(...enrichedExtras);
            }
          } catch (extraErr) {
            console.warn('[clientStore] Error fetching extra pipeline memberships (RPC path):', extraErr);
          }
        }

        // Get last cursor for next page (only from primary leads)
        const primaryLeadsRpc = enrichedLeads.filter((l: any) => !l._isExtraPipeline);
        const lastLead = primaryLeadsRpc[primaryLeadsRpc.length - 1];
        const newCursor = lastLead?.created_at || null;
        const hasMore = enrichedLeads.length === limit;

        // Merge with existing leads
        const currentLeads = get().leads;
        const existingLeadsForOtherStages = reset
          ? currentLeads.filter(l => {
              const leadStage = getEffectiveStageId(l);
              return leadStage !== stageId;
            })
          : currentLeads.filter(l => {
              const leadStage = getEffectiveStageId(l);
              return leadStage !== stageId;
            });

        const existingLeadsForThisStage = (reset || !effectiveCursor)
          ? []
          : currentLeads.filter(l => {
              const leadStage = getEffectiveStageId(l);
              return leadStage === stageId;
            });

        // Deduplicate
        const existingIds = new Set(existingLeadsForThisStage.map(l => l.id));
        const newLeads = enrichedLeads.filter((l: any) => !existingIds.has(l.id));

        const prevTotalCount = reset ? undefined : get().leadsPagination.byStage[stageId]?.totalCount;

        set({
          leads: [...existingLeadsForOtherStages, ...existingLeadsForThisStage, ...newLeads],
          leadsPagination: {
            ...get().leadsPagination,
            initialized: true,
            byStage: {
              ...get().leadsPagination.byStage,
              [stageId]: {
                cursor: newCursor,
                hasMore,
                loading: false,
                totalLoaded: existingLeadsForThisStage.length + newLeads.length,
                totalCount: totalCount ?? prevTotalCount
              }
            }
          }
        });

        return; // Exit early - RPC path complete
      }

      // ========================================
      // STANDARD PATH (no interest/tag filters)
      // ========================================

      // Build query
      let query = supabase
        .from('clients')
        .select(`
          *,
          assigned_user:user_profiles!clients_assigned_to_fkey (
            id,
            email,
            full_name
          ),
          sales_rep:user_profiles!clients_sales_rep_id_fkey (
            id,
            email,
            full_name,
            phone
          ),
          creator:user_profiles!clients_created_by_fkey (
            id,
            email,
            full_name
          ),
          referrer:referrers!clients_referrer_id_fkey (
            id,
            full_name,
            email,
            commission_rate
          ),
          partner:external_partners!clients_partner_id_fkey (
            id,
            name,
            company,
            email,
            partner_type
          ),
          pipeline:lead_pipelines!pipeline_id (
            id,
            name,
            color
          ),
          pipeline_stage:lead_pipeline_stages!pipeline_stage_id (
            id,
            name,
            color,
            sort_order
          ),
          deals:deals (
            id,
            title,
            status,
            amount
          ),
          projects (
            id,
            name,
            status
          ),
          contacts:client_contacts!client_contacts_client_id_fkey (
            id,
            person_client_id,
            name,
            phone,
            email,
            role,
            area,
            is_primary,
            is_active,
            is_referente,
            is_rappresentante_legale,
            notes,
            birth_date,
            newsletter_subscribed,
            newsletter_consent_date
          ),
          operational_locations:client_operational_locations (
            id,
            name,
            country,
            address,
            city,
            province,
            postal_code,
            region,
            is_active,
            notes,
            is_default,
            recipient_name,
            phone,
            location_type
          ),
          interests:client_interests (
            id,
            interest_category_id,
            created_at,
            interest_category:interest_categories (
              id,
              name,
              color
            )
          ),
          settori:client_settori (
            id,
            settore_id,
            created_at,
            settore:settori (
              id,
              name,
              color
            )
          )
        `)
        .eq('organization_id', organizationId);

      // Filter by lead states (is_lead = true)
      if (leadStateIds.length > 0) {
        query = query.in('stato_cliente', leadStateIds);
      }

      query = applyLeadVisibilityToQuery(query, leadVisibility);

      // Stage filter based on pipeline type
      if (isLegacyPipeline) {
        query = query.eq('lead_stage', stageId);
        // For legacy, exclude leads that have a pipeline_id
        query = query.is('pipeline_id', null);
      } else {
        query = query
          .eq('pipeline_id', pipelineId)
          .eq('pipeline_stage_id', stageId);
      }

      // Apply filters
      if (activeFilters.assignedTo) {
        query = query.eq('assigned_to', activeFilters.assignedTo);
      }
      if (activeFilters.salesRepId) {
        query = query.eq('sales_rep_id', activeFilters.salesRepId);
      }

      // Handle multiple user filters with mode
      if (activeFilters.userFilters && activeFilters.userFilters.length > 0) {
        // Group users by mode, separating __unassigned__ from real UUIDs
        const UNASSIGNED = '__unassigned__';
        const INACTIVE_OWNER = '__inactive_owner__';
        const assignedToUsers: string[] = [];
        const salesRepUsers: string[] = [];
        const bothUsers: string[] = [];
        let assignedToUnassigned = false;
        let salesRepUnassigned = false;
        let bothUnassigned = false;
        let assignedToInactive = false;
        let salesRepInactive = false;
        let bothInactive = false;

        activeFilters.userFilters.forEach(uf => {
          if (uf.mode === 'assigned_to') {
            if (uf.userId === UNASSIGNED) assignedToUnassigned = true;
            else if (uf.userId === INACTIVE_OWNER) assignedToInactive = true;
            else assignedToUsers.push(uf.userId);
          } else if (uf.mode === 'sales_rep_id') {
            if (uf.userId === UNASSIGNED) salesRepUnassigned = true;
            else if (uf.userId === INACTIVE_OWNER) salesRepInactive = true;
            else salesRepUsers.push(uf.userId);
          } else {
            if (uf.userId === UNASSIGNED) bothUnassigned = true;
            else if (uf.userId === INACTIVE_OWNER) bothInactive = true;
            else bothUsers.push(uf.userId);
          }
        });

        // Build OR filter string for Supabase
        const orConditions: string[] = [];
        if (assignedToUsers.length > 0) {
          orConditions.push(`assigned_to.in.(${assignedToUsers.join(',')})`);
        }
        if (assignedToUnassigned) {
          orConditions.push(`assigned_to.is.null`);
        }
        if (salesRepUsers.length > 0) {
          orConditions.push(`sales_rep_id.in.(${salesRepUsers.join(',')})`);
        }
        if (salesRepUnassigned) {
          orConditions.push(`sales_rep_id.is.null`);
        }
        if (bothUsers.length > 0) {
          // For 'both' mode: match either assigned_to OR sales_rep_id
          orConditions.push(`assigned_to.in.(${bothUsers.join(',')})`);
          orConditions.push(`sales_rep_id.in.(${bothUsers.join(',')})`);
        }
        if (bothUnassigned) {
          // "Non assegnata" su Entrambi vuol dire senza nessuno dei due, non
          // "manca uno dei due": in OR passavano anche i lead con assegnatario
          // ma senza commerciale, cioe' quasi tutti.
          orConditions.push('and(assigned_to.is.null,sales_rep_id.is.null)');
        }

        // "Proprietario non piu' attivo": c'e' un assegnatario, ma non e' fra gli
        // utenti attivi del menu (disattivato o cancellato). Senza la lista degli
        // attivi la voce non si puo' tradurre: resta ignorata.
        const attivi = activeFilters.activeUserIds ?? [];
        if (attivi.length > 0) {
          const nonAttivo = (col: string) => `and(${col}.not.is.null,${col}.not.in.(${attivi.join(',')}))`;
          if (assignedToInactive || bothInactive) orConditions.push(nonAttivo('assigned_to'));
          if (salesRepInactive || bothInactive) orConditions.push(nonAttivo('sales_rep_id'));
        }

        if (orConditions.length > 0) {
          query = query.or(orConditions.join(','));
        }
      }

      if (activeFilters.entityTypes && activeFilters.entityTypes.length > 0) {
        query = query.in('entity_type', activeFilters.entityTypes);
      }
      if (activeFilters.countries && activeFilters.countries.length > 0) {
        query = query.in('country', activeFilters.countries);
      }
      if (activeFilters.sources && activeFilters.sources.length > 0) {
        query = query.in('lead_source', activeFilters.sources);
      }

      // Filtro studio AlfaDocs: containment jsonb su metadata.alfadocs_links.
      // Con più studi: .or() di condizioni cs — sicuro per il parsing PostgREST
      // perché ogni JSON è a chiave singola con oggetto vuoto (nessuna virgola interna).
      if (activeFilters.alfaStudioIds && activeFilters.alfaStudioIds.length > 0) {
        if (activeFilters.alfaStudioIds.length === 1) {
          query = query.contains('metadata', { alfadocs_links: { [activeFilters.alfaStudioIds[0]]: {} } });
        } else {
          query = query.or(
            activeFilters.alfaStudioIds
              .map(pid => `metadata.cs.${JSON.stringify({ alfadocs_links: { [pid]: {} } })}`)
              .join(',')
          );
        }
      }

      // NOTE: Tag and interest filtering is handled by RPC when those filters are active
      // This standard path only runs when there are no tag/interest filters

      // Get total count for this stage (only on first page / reset)
      let totalCount: number | undefined;
      if (reset) {
        // Build count query with same filters (but no pagination/order)
        let countQuery = supabase
          .from('clients')
          .select('*', { count: 'exact', head: true })
          .eq('organization_id', organizationId);

        if (leadStateIds.length > 0) {
          countQuery = countQuery.in('stato_cliente', leadStateIds);
        }

        countQuery = applyLeadVisibilityToQuery(countQuery, leadVisibility);

        if (isLegacyPipeline) {
          countQuery = countQuery.eq('lead_stage', stageId).is('pipeline_id', null);
        } else {
          countQuery = countQuery.eq('pipeline_id', pipelineId).eq('pipeline_stage_id', stageId);
        }

        // Apply same filters as main query
        if (activeFilters.userFilters && activeFilters.userFilters.length > 0) {
          // "Non assegnata" non e' un id: va tradotto in "colonna vuota" come fa
          // la query dei dati qui sopra. Mandarlo com'e' produceva
          // `assigned_to.in.(__unassigned__)`, che PostgREST rifiuta con 400
          // (uuid non valido) facendo fallire tutto il caricamento: la pagina
          // restava vuota invece di mostrare i lead senza proprietario
          // (visto sul dev il 09/09/2026 con 7 lead senza proprietario).
          const UNASSIGNED = '__unassigned__';
          const INACTIVE_OWNER = '__inactive_owner__';
          const assignedToUsers: string[] = [];
          const salesRepUsers: string[] = [];
          const bothUsers: string[] = [];
          let assignedToUnassigned = false;
          let salesRepUnassigned = false;
          let bothUnassigned = false;
          let assignedToInactive = false;
          let salesRepInactive = false;
          let bothInactive = false;
          activeFilters.userFilters.forEach(uf => {
            if (uf.mode === 'assigned_to') {
              if (uf.userId === UNASSIGNED) assignedToUnassigned = true;
            else if (uf.userId === INACTIVE_OWNER) assignedToInactive = true;
              else assignedToUsers.push(uf.userId);
            } else if (uf.mode === 'sales_rep_id') {
              if (uf.userId === UNASSIGNED) salesRepUnassigned = true;
            else if (uf.userId === INACTIVE_OWNER) salesRepInactive = true;
              else salesRepUsers.push(uf.userId);
            } else {
              if (uf.userId === UNASSIGNED) bothUnassigned = true;
            else if (uf.userId === INACTIVE_OWNER) bothInactive = true;
              else bothUsers.push(uf.userId);
            }
          });
          const orConditions: string[] = [];
          if (assignedToUsers.length > 0) orConditions.push(`assigned_to.in.(${assignedToUsers.join(',')})`);
          if (assignedToUnassigned) orConditions.push('assigned_to.is.null');
          if (salesRepUsers.length > 0) orConditions.push(`sales_rep_id.in.(${salesRepUsers.join(',')})`);
          if (salesRepUnassigned) orConditions.push('sales_rep_id.is.null');
          if (bothUsers.length > 0) {
            orConditions.push(`assigned_to.in.(${bothUsers.join(',')})`);
            orConditions.push(`sales_rep_id.in.(${bothUsers.join(',')})`);
          }
          if (bothUnassigned) {
            orConditions.push('and(assigned_to.is.null,sales_rep_id.is.null)');
          }

          // "Proprietario non piu' attivo": c'e' un assegnatario, ma non e' fra gli
          // utenti attivi del menu (disattivato o cancellato). Senza la lista degli
          // attivi la voce non si puo' tradurre: resta ignorata.
          const attivi = activeFilters.activeUserIds ?? [];
          if (attivi.length > 0) {
            const nonAttivo = (col: string) => `and(${col}.not.is.null,${col}.not.in.(${attivi.join(',')}))`;
            if (assignedToInactive || bothInactive) orConditions.push(nonAttivo('assigned_to'));
            if (salesRepInactive || bothInactive) orConditions.push(nonAttivo('sales_rep_id'));
          }
          if (orConditions.length > 0) countQuery = countQuery.or(orConditions.join(','));
        }
        if (activeFilters.entityTypes?.length) countQuery = countQuery.in('entity_type', activeFilters.entityTypes);
        if (activeFilters.countries?.length) countQuery = countQuery.in('country', activeFilters.countries);
        if (activeFilters.sources?.length) countQuery = countQuery.in('lead_source', activeFilters.sources);

        // Filtro studio AlfaDocs: containment jsonb su metadata.alfadocs_links.
        // Con più studi: .or() di condizioni cs — sicuro per il parsing PostgREST
        // perché ogni JSON è a chiave singola con oggetto vuoto (nessuna virgola interna).
        if (activeFilters.alfaStudioIds && activeFilters.alfaStudioIds.length > 0) {
          if (activeFilters.alfaStudioIds.length === 1) {
            countQuery = countQuery.contains('metadata', { alfadocs_links: { [activeFilters.alfaStudioIds[0]]: {} } });
          } else {
            countQuery = countQuery.or(
              activeFilters.alfaStudioIds
                .map(pid => `metadata.cs.${JSON.stringify({ alfadocs_links: { [pid]: {} } })}`)
                .join(',')
            );
          }
        }

        // NOTE: Tag and interest filtering handled by RPC path

        const { count } = await countQuery;
        totalCount = count ?? undefined;
      }

      // Cursor-based pagination (effectiveCursor and sortAscending already declared above)
      if (effectiveCursor) {
        // Per ASC usiamo gt (next page ha created_at maggiori)
        // Per DESC usiamo lt (next page ha created_at minori)
        if (sortAscending) {
          query = query.gt('created_at', effectiveCursor);
        } else {
          query = query.lt('created_at', effectiveCursor);
        }
      }

      query = query
        .order('created_at', { ascending: sortAscending })
        .limit(limit);

      const { data: clientsData, error: clientsError } = await query;

      if (clientsError) throw clientsError;

      // Fetch tags for these leads
      const leadIds = clientsData?.map(c => c.id) || [];
      let tagAssignments: any[] = [];
      if (leadIds.length > 0) {
        try {
          const { data: tagsData } = await supabase
            .from('client_tag_assignments')
            .select(`
              client_id,
              assigned_at,
              client_tags!inner (
                id,
                name,
                description,
                color,
                sort_order
              )
            `)
            .eq('organization_id', organizationId)
            .eq('client_tags.is_active', true)
            .in('client_id', leadIds);

          tagAssignments = tagsData || [];
        } catch (error) {
          // Tags table not available, continue without tags
        }
      }

      // Create maps
      const stateMap = new Map(clientStates?.map(state => [state.id, state]) || []);
      const tagsMap = new Map<string, any[]>();
      tagAssignments.forEach(assignment => {
        if (!tagsMap.has(assignment.client_id)) {
          tagsMap.set(assignment.client_id, []);
        }
        tagsMap.get(assignment.client_id)!.push({ ...assignment.client_tags, assigned_at: assignment.assigned_at });
      });
      tagsMap.forEach((tags) => {
        tags.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      });

      // Enrich leads
      const enrichedLeads: any[] = clientsData?.map(client => ({
        ...client,
        client_state: client.stato_cliente ? stateMap.get(client.stato_cliente) : null,
        tags: tagsMap.get(client.id) || [],
        interest_categories: (client as any).interests?.map((i: any) => i.interest_category_id).filter(Boolean) || []
      })) || [];

      // ========================================
      // MULTI-PIPELINE: fetch extra leads from junction table
      // Only on first page (reset) and non-legacy pipelines
      // ========================================
      if (!isLegacyPipeline && pipelineId && (reset || !effectiveCursor)) {
        try {
          const primaryIds = new Set(enrichedLeads.map(l => l.id));

          // Get extra memberships for this pipeline/stage (fetch condivisa per pipeline)
          const extraMemberships = (await fetchPipelineMembershipsCached(pipelineId))
            .filter(m => m.pipeline_stage_id === stageId);

          const extraClientIds = (extraMemberships || [])
            .filter(m => !primaryIds.has(m.client_id))
            .map(m => m.client_id);

          if (extraClientIds.length > 0) {
            // Fetch full client data for extra members
            let extraQuery = supabase
              .from('clients')
              .select(`
                *,
                assigned_user:user_profiles!clients_assigned_to_fkey (id, email, full_name),
                sales_rep:user_profiles!clients_sales_rep_id_fkey (id, email, full_name, phone),
                creator:user_profiles!clients_created_by_fkey (id, email, full_name),
                referrer:referrers!clients_referrer_id_fkey (id, full_name, email, commission_rate),
                partner:external_partners!clients_partner_id_fkey (id, name, company, email, partner_type),
                pipeline:lead_pipelines!pipeline_id (id, name, color),
                pipeline_stage:lead_pipeline_stages!pipeline_stage_id (id, name, color, sort_order),
                deals:deals (id, title, status, amount),
                projects (id, name, status),
                contacts:client_contacts!client_contacts_client_id_fkey (id, person_client_id, name, phone, email, role, area, is_primary, is_active, is_referente, is_rappresentante_legale, notes, birth_date, newsletter_subscribed, newsletter_consent_date)
              `)
              .in('id', extraClientIds);

            // Apply tipologia filter to extras too
            if (stateFilterIds.length > 0) {
              extraQuery = extraQuery.in('stato_cliente', stateFilterIds);
            }

            const { data: extraClientsData } = await extraQuery;

            // Enrich extra leads and mark as extra
            const membershipMap = new Map((extraMemberships || []).map(m => [m.client_id, m]));
            const enrichedExtras = (extraClientsData || []).map(client => ({
              ...client,
              client_state: client.stato_cliente ? stateMap.get(client.stato_cliente) : null,
              tags: tagsMap.get(client.id) || [],
              interest_categories: [],
              _isExtraPipeline: true,
              _membershipId: membershipMap.get(client.id)?.id,
              _membershipStageId: stageId
            }));

            enrichedLeads.push(...enrichedExtras);
          }
        } catch (extraErr) {
          console.warn('[clientStore] Error fetching extra pipeline memberships:', extraErr);
        }
      }

      // Get last cursor for next page (only from primary leads, not extras)
      const primaryLeads = enrichedLeads.filter(l => !l._isExtraPipeline);
      const lastLead = primaryLeads[primaryLeads.length - 1];
      const newCursor = lastLead?.created_at || null;
      const hasMore = enrichedLeads.length === limit;

      // IMPORTANT: Read 'leads' NOW (after await) to get the current state
      // This avoids race condition when multiple stages are fetched in parallel
      const currentLeads = get().leads;

      // Merge with existing leads or reset
      const existingLeadsForOtherStages = reset
        ? currentLeads.filter(l => {
            const leadStage = getEffectiveStageId(l);
            return leadStage !== stageId;
          })
        : currentLeads.filter(l => {
            const leadStage = getEffectiveStageId(l);
            return leadStage !== stageId;
          });

      // Get existing leads for this stage (only if not reset and cursor exists)
      const existingLeadsForThisStage = (reset || !effectiveCursor)
        ? []
        : currentLeads.filter(l => {
            const leadStage = getEffectiveStageId(l);
            return leadStage === stageId;
          });

      // Deduplicate
      const existingIds = new Set(existingLeadsForThisStage.map(l => l.id));
      const newLeads = enrichedLeads.filter(l => !existingIds.has(l.id));

      // Get previous totalCount if not reset (keep it from previous fetch)
      const prevTotalCount = reset ? undefined : get().leadsPagination.byStage[stageId]?.totalCount;

      set({
        leads: [...existingLeadsForOtherStages, ...existingLeadsForThisStage, ...newLeads],
        leadsPagination: {
          ...get().leadsPagination,
          initialized: true,
          byStage: {
            ...get().leadsPagination.byStage,
            [stageId]: {
              cursor: newCursor,
              hasMore,
              loading: false,
              totalLoaded: existingLeadsForThisStage.length + newLeads.length,
              totalCount: totalCount ?? prevTotalCount  // Use new count if available, otherwise keep previous
            }
          }
        }
      });

    } catch (error) {
      console.error(`Error fetching leads for stage ${stageId}:`, error);
      set({
        leadsPagination: {
          ...get().leadsPagination,
          byStage: {
            ...get().leadsPagination.byStage,
            [stageId]: { ...stagePagination, loading: false }
          }
        }
      });
    }
  },

  fetchMoreLeadsByStage: async (stageId, pipelineId) => {
    const { leadsPagination } = get();
    const stagePagination = leadsPagination.byStage[stageId];

    // If no pagination state or no more items, skip
    if (!stagePagination || !stagePagination.hasMore || stagePagination.loading) {
      return;
    }

    // Use the stored cursor to fetch next page
    await get().fetchLeadsByStage(stageId, pipelineId, {
      cursor: stagePagination.cursor || undefined,
      limit: leadsPagination.pageSize
    });
  },

  // Initialize pagination state from leads already loaded by fetchLeads()
  // This allows infinite scroll to work with the existing data without re-fetching
  initializePaginationFromExistingLeads: (stages, pipelineId) => {
    const { leads, leadsPagination } = get();
    const isLegacyPipeline = !pipelineId || pipelineId === 'legacy';

    // Group leads by stage and find the oldest one (cursor) for each
    const byStage: Record<string, StagePaginationState> = {};

    stages.forEach(stage => {
      // Find leads belonging to this stage
      const stageLeads = leads.filter(lead => {
        const leadStageId = lead.pipeline_id ? lead.pipeline_stage_id : lead.lead_stage;
        return leadStageId === stage.id;
      });

      // Sort by created_at descending to find the oldest (last) one
      const sortedLeads = [...stageLeads].sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      const oldestLead = sortedLeads[sortedLeads.length - 1];
      const cursor = oldestLead?.created_at || null;

      // Assume hasMore=true if we have leads in this stage
      // The actual value will be determined when user tries to load more
      // We set hasMore=true because the initial 1000 limit might have cut off leads
      byStage[stage.id] = {
        cursor,
        hasMore: stageLeads.length > 0, // Conservative: assume there might be more
        loading: false,
        totalLoaded: stageLeads.length
      };
    });

    set({
      leadsPagination: {
        ...leadsPagination,
        initialized: true,
        byStage
      }
    });
  }
}));